'use client';
import React, { useState, useEffect } from 'react';

interface ResourceLink {
  title: string;
  url?: string;
  desc: string;
  panelId?: string;
}

interface NewMuslimCategory {
  id: string;
  icon: string;
  title: string;
  arabic: string;
  subtitle: string;
  nameOfAllah: string;
  arabicName: string;
  meaning: string;
  ayat: string;
  ayatRef: string;
  ayatTrans: string;
  fisabilillah: string;
  intro: string;
  resources: ResourceLink[];
}

interface SidePanel {
  id: string;
  title: string;
  content: React.ReactNode;
}

const CATEGORIES: NewMuslimCategory[] = [
  {
    id: 'learn-prayer',
    icon: '🕌',
    title: 'Learn Prayer (Salah)',
    arabic: 'الصلاة',
    subtitle: 'The Second Pillar of Islam',
    nameOfAllah: 'AS-SAMI',
    arabicName: 'السَّمِيع',
    meaning: 'The All-Hearing',
    ayat: 'وَأَقِيمُوا الصَّلَاةَ وَآتُوا الزَّكَاةَ وَارْكَعُوا مَعَ الرَّاكِعِينَ',
    ayatRef: 'Quran 2:43',
    ayatTrans: '"Establish prayer, give zakah, and bow with those who bow."',
    fisabilillah: 'Salah is the direct line between the servant and the Lord. Allah hears every whisper of the heart, every movement of the lips, every prostration of the body. As-Sami — the All-Hearing — receives your prayer before you complete it. For the new Muslim, learning to pray is not merely a ritual — it is learning the language of the soul. Every raka\'ah is a conversation. Every sujood is a return. Fisabilillah.',
    intro: 'Salah is the direct connection between the believer and Allah. Prayed five times daily, it is the pillar that holds the deen upright. Learning to pray correctly is one of the first and most important steps for a new Muslim.',
    resources: [
      { title: 'How to Pray — Step by Step Guide', panelId: 'how-to-pray-steps', desc: 'Complete illustrated guide to the five daily prayers with transliterations and video' },
      { title: 'How to Pray — SeekersGuidance', panelId: 'how-to-pray-seekers', desc: 'Detailed fiqh guide to the prayer with step-by-step video instruction' },
      { title: 'Salah — Yaqeen Institute', panelId: 'salah-yaqeen', desc: 'Research and reflections on the spiritual dimensions of prayer' },
      { title: 'Prayer Times — Masjid Al-Ihsan', url: '#prayer-times', desc: 'Daily prayer times for Masjid Al-Ihsan in Bronzeville, Chicago' },
      { title: 'Salah App — Muslim Pro', url: 'https://www.muslimpro.com/', desc: 'Prayer times, Qibla direction, and Adhan reminders for your location' },
    ],
  },
  {
    id: 'wudu',
    icon: '💧',
    title: 'Wudu (Ritual Purification)',
    arabic: 'الوضوء',
    subtitle: 'Purification Before Prayer',
    nameOfAllah: 'AT-TAWWAB',
    arabicName: 'التَّوَّاب',
    meaning: 'The Ever-Accepting of Repentance',
    ayat: 'إِنَّ اللَّهَ يُحِبُّ التَّوَّابِينَ وَيُحِبُّ الْمُتَطَهِّرِينَ',
    ayatRef: 'Quran 2:222',
    ayatTrans: '"Indeed, Allah loves those who are constantly repentant and loves those who purify themselves."',
    fisabilillah: 'Wudu is not merely the washing of limbs — it is the renewal of the covenant. At-Tawwab, the Ever-Accepting of Repentance, has made purification a gateway to His presence. The Prophet ﷺ said that sins fall away with the water of wudu. For the new Muslim, each wudu is a fresh beginning — a physical act that mirrors the spiritual reality of turning toward Allah. Cleanliness is half of faith. Fisabilillah.',
    intro: 'Wudu is the ritual purification performed before prayer. It is a physical and spiritual act of cleansing that prepares the believer to stand before Allah. The Prophet ﷺ said: "Cleanliness is half of faith."',
    resources: [
      { title: 'How to Make Wudu — Step by Step', panelId: 'how-to-pray-steps', desc: 'Illustrated guide to performing wudu correctly — included in the How to Pray guide' },
      { title: 'Wudu Guide — SeekersGuidance', url: 'https://seekersguidance.org/answers/hanafi-fiqh/wudu-ablution-complete-guide/', desc: 'Detailed fiqh guide to wudu including what breaks it' },
      { title: 'Tayammum (Dry Purification)', url: 'https://islamqa.info/en/answers/9427/how-to-do-tayammum', desc: 'How to perform dry purification when water is unavailable' },
      { title: 'Ghusl (Full Purification)', url: 'https://seekersguidance.org/articles/fiqh/ghusl-the-purificatory-bath/', desc: 'Guide to the full ritual bath and when it is required' },
    ],
  },
  {
    id: 'five-pillars',
    icon: '🏛️',
    title: 'Five Pillars of Islam',
    arabic: 'أركان الإسلام',
    subtitle: 'The Foundation of the Deen',
    nameOfAllah: 'AL-MATIN',
    arabicName: 'الْمَتِين',
    meaning: 'The Firm, The Steadfast',
    ayat: 'شَهِدَ اللَّهُ أَنَّهُ لَا إِلَٰهَ إِلَّا هُوَ وَالْمَلَائِكَةُ وَأُولُو الْعِلْمِ قَائِمًا بِالْقِسْطِ',
    ayatRef: 'Quran 3:18',
    ayatTrans: '"Allah witnesses that there is no deity except Him, and so do the angels and those of knowledge — standing in justice."',
    fisabilillah: 'The Five Pillars are not a checklist — they are a complete architecture of the human being\'s relationship with Allah. Al-Matin, the Firm and Steadfast, has given us a structure that holds through every storm. Shahada is the foundation. Salah is the pillar. Zakat is the circulation. Sawm is the purification. Hajj is the return. Together they form the complete human being — grounded, connected, purified, and oriented toward Allah. Fisabilillah.',
    intro: 'The Five Pillars are the core acts of worship that form the foundation of a Muslim\'s life. They are: Shahada (testimony of faith), Salah (prayer), Zakat (charity), Sawm (fasting), and Hajj (pilgrimage). Each pillar is a complete act of worship and a way of life.',
    resources: [
      { title: 'Five Pillars — Full Overview', panelId: 'five-pillars-detail', desc: 'Detailed breakdown of all 5 pillars with resources for each' },
      { title: '99 Names of Allah — Al-Asma al-Husna', panelId: 'ninety-nine-names', desc: 'The 99 Beautiful Names of Allah — a foundational resource for understanding Tawhid' },
      { title: 'New Muslim Guide — WhyIslam', url: 'https://www.whyislam.org/new-muslims/', desc: 'Complete new Muslim resource covering all five pillars and more' },
      { title: 'New Muslim Academy', url: 'https://newmuslimacademy.org/', desc: 'Structured online curriculum for new Muslims covering all pillars' },
    ],
  },
  {
    id: 'six-pillars-iman',
    icon: '✨',
    title: 'Six Pillars of Iman',
    arabic: 'أركان الإيمان',
    subtitle: 'The Articles of Faith',
    nameOfAllah: 'AL-HAQQ',
    arabicName: 'الْحَقّ',
    meaning: 'The Absolute Truth',
    ayat: 'آمَنَ الرَّسُولُ بِمَا أُنزِلَ إِلَيْهِ مِن رَّبِّهِ وَالْمُؤْمِنُونَ ۚ كُلٌّ آمَنَ بِاللَّهِ وَمَلَائِكَتِهِ وَكُتُبِهِ وَرُسُلِهِ',
    ayatRef: 'Quran 2:285',
    ayatTrans: '"The Messenger has believed in what was revealed to him from his Lord, and so have the believers. All of them have believed in Allah and His angels and His books and His messengers."',
    fisabilillah: 'Al-Haqq — the Absolute Truth — is the foundation of Iman. Belief is not merely intellectual assent; it is the orientation of the entire being toward what is real. The six pillars of Iman map the full landscape of the unseen: Allah, His angels, His books, His messengers, the Last Day, and divine decree. For the new Muslim, building this inner architecture is the work of a lifetime — and it begins with the first sincere utterance of the Shahada. Fisabilillah.',
    intro: 'The Six Pillars of Iman are the core beliefs of Islam: Belief in Allah, His Angels, His Books, His Messengers, the Last Day, and Divine Decree (Qadar). These beliefs form the inner foundation of the Muslim\'s heart and worldview.',
    resources: [
      { title: 'Six Pillars of Iman — Full Guide', panelId: 'six-pillars-iman', desc: 'Detailed breakdown of all 6 pillars of faith with Quranic evidence and explanations' },
      { title: 'Qadar — Divine Decree', url: 'https://yaqeeninstitute.org/read/paper/divine-decree-and-human-agency', desc: 'Understanding Allah\'s divine decree and human free will' },
      { title: 'The Last Day — Yaqeen Institute', url: 'https://yaqeeninstitute.org/topics/afterlife', desc: 'Research papers on the afterlife, resurrection, and the Day of Judgment' },
    ],
  },
  {
    id: 'marriage',
    icon: '💍',
    title: 'Marriage (Nikah)',
    arabic: 'النكاح',
    subtitle: 'Islamic Marriage & Family',
    nameOfAllah: 'AL-WADUD',
    arabicName: 'الْوَدُود',
    meaning: 'The Loving, The Affectionate',
    ayat: 'وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ بَيْنَكُم مَّوَدَّةً وَرَحْمَةً',
    ayatRef: 'Quran 30:21',
    ayatTrans: '"And of His signs is that He created for you from yourselves mates that you may find tranquility in them; and He placed between you affection and mercy."',
    fisabilillah: 'Al-Wadud — the Loving — placed mawaddah and rahmah between spouses as a sign of His own nature. Marriage in Islam is not merely a social contract; it is a divine covenant (mithaq) that mirrors the relationship between the believer and Allah. The Prophet ﷺ said: "Marriage is half of the deen." For the new Muslim, understanding the rights, responsibilities, and spiritual dimensions of Islamic marriage is essential for building a home that is a place of sakina — tranquility. Fisabilillah.',
    intro: 'Marriage in Islam is a sacred covenant (mithaq) and an act of worship. The Prophet ﷺ said: "Marriage is half of the deen." Understanding the rights, responsibilities, and spiritual dimensions of Islamic marriage is essential for building a strong Muslim family.',
    resources: [
      { title: 'Islamic Marriage — Full Guide', panelId: 'marriage-panel', desc: 'Complete guide to the Nikah, rights and responsibilities, and building an Islamic home' },
      { title: 'Nikah at Masjid Al-Ihsan', url: '/subhaal/masjid-al-ihsan/contact', desc: 'Contact Masjid Al-Ihsan to arrange an Islamic marriage ceremony' },
      { title: 'Pre-Marital Counseling — Chicago', url: 'https://darulqasim.org/', desc: 'Darul Qasim Chicago offers pre-marital Islamic counseling' },
    ],
  },
  {
    id: 'janazah',
    icon: '🤲',
    title: 'Janazah (Funeral Prayer)',
    arabic: 'الجنازة',
    subtitle: 'Islamic Funeral Rites',
    nameOfAllah: 'AL-HAYY',
    arabicName: 'الْحَيّ',
    meaning: 'The Ever-Living',
    ayat: 'كُلُّ نَفْسٍ ذَائِقَةُ الْمَوْتِ ۗ وَإِنَّمَا تُوَفَّوْنَ أُجُورَكُمْ يَوْمَ الْقِيَامَةِ',
    ayatRef: 'Quran 3:185',
    ayatTrans: '"Every soul will taste death, and you will only be given your full compensation on the Day of Resurrection."',
    fisabilillah: 'Al-Hayy — the Ever-Living — reminds us that death is not an ending but a transition. The Janazah prayer is a collective obligation (fard kifayah) upon the Muslim community — a final act of brotherhood and sisterhood for the one who has returned to Allah. Understanding Islamic funeral rites — from ghusl to burial — is an act of love for our community and preparation for our own return. Every Muslim community must be equipped to honor its dead. Fisabilillah.',
    intro: 'The Janazah prayer is a collective obligation (fard kifayah) upon the Muslim community. Understanding Islamic funeral rites — from ghusl (washing) to burial — is an important part of community responsibility and honoring our deceased.',
    resources: [
      { title: 'Janazah — Complete Guide', panelId: 'janazah-panel', desc: 'Step-by-step guide to Islamic funeral rites: ghusl, kafan, Janazah prayer, and burial' },
      { title: 'Janazah Services — Masjid Al-Ihsan', url: '/subhaal/masjid-al-ihsan/contact', desc: 'Contact Masjid Al-Ihsan for Janazah services in the Chicago area' },
    ],
  },
  {
    id: 'aqidah-fiqh',
    icon: '📖',
    title: 'Aqidah & Fiqh',
    arabic: 'العقيدة والفقه',
    subtitle: 'Theology & Islamic Law',
    nameOfAllah: 'AL-ALIM',
    arabicName: 'الْعَلِيم',
    meaning: 'The All-Knowing',
    ayat: 'اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ',
    ayatRef: 'Quran 96:1',
    ayatTrans: '"Read in the name of your Lord who created."',
    fisabilillah: 'Al-Alim — the All-Knowing — revealed the Quran as a complete guidance for humanity. The first command of revelation was to read. The Quran is not merely a book to be recited — it is a living guidance to be understood, internalized, and embodied. The Hadith are the living commentary of the Prophet ﷺ on that guidance. For the new Muslim, beginning the journey of Quran and Hadith is the beginning of a lifelong conversation with Allah and His Messenger. Fisabilillah.',
    intro: 'Aqidah is the science of Islamic belief and theology. Fiqh is Islamic jurisprudence — the practical rulings that govern worship and daily life. Both are essential for a grounded and practiced Muslim life.',
    resources: [
      { title: 'Aqidah & Fiqh — Full Guide', panelId: 'aqidah-fiqh-panel', desc: 'Introduction to Islamic theology and jurisprudence with video resources and study paths' },
      { title: 'Fiqh of Worship — SeekersGuidance', url: 'https://seekersguidance.org/courses/hanafi-fiqh-of-worship/', desc: 'Free online course on the fiqh of purification, prayer, and fasting' },
      { title: 'Darul Qasim — Chicago', url: 'https://darulqasim.org/', desc: 'Chicago-based Islamic seminary offering courses in aqidah and fiqh' },
    ],
  },
  {
    id: 'quran-hadith',
    icon: '📚',
    title: 'Quran & Hadith',
    arabic: 'القرآن والحديث',
    subtitle: 'The Primary Sources of Islam',
    nameOfAllah: 'AL-ALIM',
    arabicName: 'الْعَلِيم',
    meaning: 'The All-Knowing',
    ayat: 'اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ',
    ayatRef: 'Quran 96:1',
    ayatTrans: '"Read in the name of your Lord who created."',
    fisabilillah: 'Al-Alim — the All-Knowing — revealed the Quran as a complete guidance for humanity. The first command of revelation was to read. The Quran is not merely a book to be recited — it is a living guidance to be understood, internalized, and embodied. The Hadith are the living commentary of the Prophet ﷺ on that guidance. For the new Muslim, beginning the journey of Quran and Hadith is the beginning of a lifelong conversation with Allah and His Messenger. Fisabilillah.',
    intro: 'The Quran is the direct word of Allah, and the Hadith are the recorded sayings and actions of the Prophet Muhammad ﷺ. Together they are the primary sources of Islamic guidance.',
    resources: [
      { title: 'Quran & Hadith — Full Guide', panelId: 'quran-hadith-panel', desc: 'Complete guide to beginning your Quran and Hadith journey with video resources and study tools' },
      { title: 'Quran.com — Read & Listen', url: 'https://quran.com/', desc: 'Read the Quran in Arabic with translations and audio recitation' },
      { title: 'Sunnah.com — Hadith Collections', url: 'https://sunnah.com/', desc: 'Searchable database of authentic hadith collections' },
      { title: '40 Hadith Nawawi', url: 'https://sunnah.com/nawawi40', desc: 'The essential 40 hadith collection — foundational for every Muslim' },
    ],
  },
  {
    id: 'zakat',
    icon: '🌿',
    title: 'Zakat Applications',
    arabic: 'الزكاة',
    subtitle: 'Obligatory Charity & Applications',
    nameOfAllah: 'AL-KARIM',
    arabicName: 'الْكَرِيم',
    meaning: 'The Most Generous',
    ayat: 'وَأَقِيمُوا الصَّلَاةَ وَآتُوا الزَّكَاةَ وَمَا تُقَدِّمُوا لِأَنفُسِكُم مِّنْ خَيْرٍ تَجِدُوهُ عِندَ اللَّهِ',
    ayatRef: 'Quran 2:110',
    ayatTrans: '"Establish prayer and give zakah, and whatever good you put forward for yourselves — you will find it with Allah."',
    fisabilillah: 'Al-Karim — the Most Generous — has made generosity a pillar of the deen. Zakat is not charity in the conventional sense — it is the purification of wealth, the right of the poor in the property of the rich, and the circulation of divine provision through the community. For those in need, Zakat is a right — not a handout. For those with qualifying wealth, it is an obligation — not a choice. Fisabilillah.',
    intro: 'Zakat is the third pillar of Islam — obligatory annual charity given on qualifying wealth. It purifies wealth and redistributes resources within the community.',
    resources: [
      { title: 'Zakat — Full Guide', panelId: 'zakat-panel', desc: 'Complete guide to Zakat: who must pay, how to calculate, who can receive, and how to apply' },
      { title: 'Zakat Calculator — NZF', url: 'https://nzfusa.org/zakat-calculator/', desc: 'Calculate your Zakat obligation based on your assets' },
      { title: 'Zakat Application — ICNA Relief', url: 'https://icnarelief.org/zakat/', desc: 'Apply for Zakat assistance through ICNA Relief' },
      { title: 'Zakat at Masjid Al-Ihsan', url: '/subhaal/masjid-al-ihsan/contact', desc: 'Contact Masjid Al-Ihsan to apply for or give Zakat locally' },
    ],
  },
];

// ── 99 Names of Allah data ──
const NAMES_OF_ALLAH = [
  { num: 1, arabic: 'الرَّحْمَن', transliteration: 'Ar-Rahman', meaning: 'The Most Gracious' },
  { num: 2, arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Most Merciful' },
  { num: 3, arabic: 'الْمَلِك', transliteration: 'Al-Malik', meaning: 'The King, The Sovereign' },
  { num: 4, arabic: 'الْقُدُّوس', transliteration: 'Al-Quddus', meaning: 'The Most Holy' },
  { num: 5, arabic: 'السَّلَام', transliteration: 'As-Salam', meaning: 'The Source of Peace' },
  { num: 6, arabic: 'الْمُؤْمِن', transliteration: 'Al-Mu\'min', meaning: 'The Granter of Security' },
  { num: 7, arabic: 'الْمُهَيْمِن', transliteration: 'Al-Muhaymin', meaning: 'The Protector' },
  { num: 8, arabic: 'الْعَزِيز', transliteration: 'Al-Aziz', meaning: 'The Almighty' },
  { num: 9, arabic: 'الْجَبَّار', transliteration: 'Al-Jabbar', meaning: 'The Compeller' },
  { num: 10, arabic: 'الْمُتَكَبِّر', transliteration: 'Al-Mutakabbir', meaning: 'The Supreme' },
  { num: 11, arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'The Creator' },
  { num: 12, arabic: 'الْبَارِئ', transliteration: 'Al-Bari\'', meaning: 'The Evolver' },
  { num: 13, arabic: 'الْمُصَوِّر', transliteration: 'Al-Musawwir', meaning: 'The Fashioner of Forms' },
  { num: 14, arabic: 'الْغَفَّار', transliteration: 'Al-Ghaffar', meaning: 'The Repeatedly Forgiving' },
  { num: 15, arabic: 'الْقَهَّار', transliteration: 'Al-Qahhar', meaning: 'The Subduer' },
  { num: 16, arabic: 'الْوَهَّاب', transliteration: 'Al-Wahhab', meaning: 'The Bestower' },
  { num: 17, arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider' },
  { num: 18, arabic: 'الْفَتَّاح', transliteration: 'Al-Fattah', meaning: 'The Opener' },
  { num: 19, arabic: 'الْعَلِيم', transliteration: 'Al-Alim', meaning: 'The All-Knowing' },
  { num: 20, arabic: 'الْقَابِض', transliteration: 'Al-Qabid', meaning: 'The Withholder' },
  { num: 21, arabic: 'الْبَاسِط', transliteration: 'Al-Basit', meaning: 'The Extender' },
  { num: 22, arabic: 'الْخَافِض', transliteration: 'Al-Khafid', meaning: 'The Reducer' },
  { num: 23, arabic: 'الرَّافِع', transliteration: 'Ar-Rafi\'', meaning: 'The Exalter' },
  { num: 24, arabic: 'الْمُعِز', transliteration: 'Al-Mu\'izz', meaning: 'The Honourer' },
  { num: 25, arabic: 'الْمُذِل', transliteration: 'Al-Mudhill', meaning: 'The Dishonourer' },
  { num: 26, arabic: 'السَّمِيع', transliteration: 'As-Sami\'', meaning: 'The All-Hearing' },
  { num: 27, arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'The All-Seeing' },
  { num: 28, arabic: 'الْحَكَم', transliteration: 'Al-Hakam', meaning: 'The Judge' },
  { num: 29, arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'The Just' },
  { num: 30, arabic: 'اللَّطِيف', transliteration: 'Al-Latif', meaning: 'The Subtle One' },
  { num: 31, arabic: 'الْخَبِير', transliteration: 'Al-Khabir', meaning: 'The All-Aware' },
  { num: 32, arabic: 'الْحَلِيم', transliteration: 'Al-Halim', meaning: 'The Forbearing' },
  { num: 33, arabic: 'الْعَظِيم', transliteration: 'Al-Azim', meaning: 'The Magnificent' },
  { num: 34, arabic: 'الْغَفُور', transliteration: 'Al-Ghafur', meaning: 'The Forgiving' },
  { num: 35, arabic: 'الشَّكُور', transliteration: 'Ash-Shakur', meaning: 'The Appreciative' },
  { num: 36, arabic: 'الْعَلِيّ', transliteration: 'Al-Ali', meaning: 'The Most High' },
  { num: 37, arabic: 'الْكَبِير', transliteration: 'Al-Kabir', meaning: 'The Most Great' },
  { num: 38, arabic: 'الْحَفِيظ', transliteration: 'Al-Hafiz', meaning: 'The Preserver' },
  { num: 39, arabic: 'الْمُقِيت', transliteration: 'Al-Muqit', meaning: 'The Nourisher' },
  { num: 40, arabic: 'الْحَسِيب', transliteration: 'Al-Hasib', meaning: 'The Reckoner' },
  { num: 41, arabic: 'الْجَلِيل', transliteration: 'Al-Jalil', meaning: 'The Majestic' },
  { num: 42, arabic: 'الْكَرِيم', transliteration: 'Al-Karim', meaning: 'The Most Generous' },
  { num: 43, arabic: 'الرَّقِيب', transliteration: 'Ar-Raqib', meaning: 'The Watchful' },
  { num: 44, arabic: 'الْمُجِيب', transliteration: 'Al-Mujib', meaning: 'The Responsive' },
  { num: 45, arabic: 'الْوَاسِع', transliteration: 'Al-Wasi\'', meaning: 'The All-Encompassing' },
  { num: 46, arabic: 'الْحَكِيم', transliteration: 'Al-Hakim', meaning: 'The Perfectly Wise' },
  { num: 47, arabic: 'الْوَدُود', transliteration: 'Al-Wadud', meaning: 'The Loving' },
  { num: 48, arabic: 'الْمَجِيد', transliteration: 'Al-Majid', meaning: 'The Glorious' },
  { num: 49, arabic: 'الْبَاعِث', transliteration: 'Al-Ba\'ith', meaning: 'The Resurrector' },
  { num: 50, arabic: 'الشَّهِيد', transliteration: 'Ash-Shahid', meaning: 'The Witness' },
  { num: 51, arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'The Truth' },
  { num: 52, arabic: 'الْوَكِيل', transliteration: 'Al-Wakil', meaning: 'The Trustee' },
  { num: 53, arabic: 'الْقَوِيّ', transliteration: 'Al-Qawi', meaning: 'The Most Strong' },
  { num: 54, arabic: 'الْمَتِين', transliteration: 'Al-Matin', meaning: 'The Firm' },
  { num: 55, arabic: 'الْوَلِيّ', transliteration: 'Al-Wali', meaning: 'The Protecting Friend' },
  { num: 56, arabic: 'الْحَمِيد', transliteration: 'Al-Hamid', meaning: 'The Source of Goodness' },
  { num: 57, arabic: 'الْمُحْصِي', transliteration: 'Al-Muhsi', meaning: 'The Accounter' },
  { num: 58, arabic: 'الْمُبْدِئ', transliteration: 'Al-Mubdi\'', meaning: 'The Originator' },
  { num: 59, arabic: 'الْمُعِيد', transliteration: 'Al-Mu\'id', meaning: 'The Restorer' },
  { num: 60, arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life' },
  { num: 61, arabic: 'الْمُمِيت', transliteration: 'Al-Mumit', meaning: 'The Taker of Life' },
  { num: 62, arabic: 'الْحَيّ', transliteration: 'Al-Hayy', meaning: 'The Ever-Living' },
  { num: 63, arabic: 'الْقَيُّوم', transliteration: 'Al-Qayyum', meaning: 'The Self-Subsisting' },
  { num: 64, arabic: 'الْوَاحِد', transliteration: 'Al-Wahid', meaning: 'The One' },
  { num: 65, arabic: 'الأَحَد', transliteration: 'Al-Ahad', meaning: 'The Unique' },
  { num: 66, arabic: 'الصَّمَد', transliteration: 'As-Samad', meaning: 'The Eternal' },
  { num: 67, arabic: 'الْقَادِر', transliteration: 'Al-Qadir', meaning: 'The Capable' },
  { num: 68, arabic: 'الْمُقْتَدِر', transliteration: 'Al-Muqtadir', meaning: 'The Powerful' },
  { num: 69, arabic: 'الْمُقَدِّم', transliteration: 'Al-Muqaddim', meaning: 'The Expediter' },
  { num: 70, arabic: 'الْمُؤَخِّر', transliteration: 'Al-Mu\'akhkhir', meaning: 'The Delayer' },
  { num: 71, arabic: 'الأَوَّل', transliteration: 'Al-Awwal', meaning: 'The First' },
  { num: 72, arabic: 'الآخِر', transliteration: 'Al-Akhir', meaning: 'The Last' },
  { num: 73, arabic: 'الظَّاهِر', transliteration: 'Az-Zahir', meaning: 'The Manifest' },
  { num: 74, arabic: 'الْبَاطِن', transliteration: 'Al-Batin', meaning: 'The Hidden' },
  { num: 75, arabic: 'الْوَالِي', transliteration: 'Al-Wali', meaning: 'The Governor' },
  { num: 76, arabic: 'الْمُتَعَالِي', transliteration: 'Al-Muta\'ali', meaning: 'The Most Exalted' },
  { num: 77, arabic: 'الْبَرّ', transliteration: 'Al-Barr', meaning: 'The Source of Goodness' },
  { num: 78, arabic: 'التَّوَّاب', transliteration: 'At-Tawwab', meaning: 'The Ever-Returning' },
  { num: 79, arabic: 'الْمُنْتَقِم', transliteration: 'Al-Muntaqim', meaning: 'The Avenger' },
  { num: 80, arabic: 'الْعَفُوّ', transliteration: 'Al-Afuww', meaning: 'The Pardoner' },
  { num: 81, arabic: 'الرَّؤُوف', transliteration: 'Ar-Ra\'uf', meaning: 'The Compassionate' },
  { num: 82, arabic: 'مَالِكُ الْمُلْك', transliteration: 'Malik-ul-Mulk', meaning: 'Owner of All Sovereignty' },
  { num: 83, arabic: 'ذُو الْجَلَالِ وَالإِكْرَام', transliteration: 'Dhul-Jalali-wal-Ikram', meaning: 'Lord of Majesty and Generosity' },
  { num: 84, arabic: 'الْمُقْسِط', transliteration: 'Al-Muqsit', meaning: 'The Equitable' },
  { num: 85, arabic: 'الْجَامِع', transliteration: 'Al-Jami\'', meaning: 'The Gatherer' },
  { num: 86, arabic: 'الْغَنِيّ', transliteration: 'Al-Ghani', meaning: 'The Self-Sufficient' },
  { num: 87, arabic: 'الْمُغْنِي', transliteration: 'Al-Mughni', meaning: 'The Enricher' },
  { num: 88, arabic: 'الْمَانِع', transliteration: 'Al-Mani\'', meaning: 'The Preventer' },
  { num: 89, arabic: 'الضَّار', transliteration: 'Ad-Darr', meaning: 'The Distresser' },
  { num: 90, arabic: 'النَّافِع', transliteration: 'An-Nafi\'', meaning: 'The Propitious' },
  { num: 91, arabic: 'النُّور', transliteration: 'An-Nur', meaning: 'The Light' },
  { num: 92, arabic: 'الْهَادِي', transliteration: 'Al-Hadi', meaning: 'The Guide' },
  { num: 93, arabic: 'الْبَدِيع', transliteration: 'Al-Badi\'', meaning: 'The Incomparable' },
  { num: 94, arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting' },
  { num: 95, arabic: 'الْوَارِث', transliteration: 'Al-Warith', meaning: 'The Inheritor' },
  { num: 96, arabic: 'الرَّشِيد', transliteration: 'Ar-Rashid', meaning: 'The Guide to the Right Path' },
  { num: 97, arabic: 'الصَّبُور', transliteration: 'As-Sabur', meaning: 'The Patient' },
];

const FIVE_PILLARS = [
  {
    num: 1,
    arabic: 'الشَّهَادَة',
    name: 'Shahada',
    subtitle: 'The Declaration of Faith',
    nameOfAllah: 'AL-AHAD',
    arabicName: 'الأَحَد',
    meaning: 'The Unique, The One',
    ayat: 'شَهِدَ اللَّهُ أَنَّهُ لَا إِلَٰهَ إِلَّا هُوَ',
    ayatRef: 'Quran 112:1',
    ayatTrans: '"Allah witnesses that there is no deity except Him."',
    description: 'The Shahada is the first and most fundamental pillar of Islam. It is the declaration: "Ash-hadu an la ilaha ill-Allah, wa ash-hadu anna Muhammadan rasul-Allah" — I bear witness that there is no god but Allah, and I bear witness that Muhammad is the Messenger of Allah. This single statement is the gateway into Islam and the foundation of the Muslim\'s entire worldview. It is not merely a verbal declaration — it is a complete reorientation of the self toward the One.',
    resources: [
      { title: 'Understanding the Shahada — SeekersGuidance', url: 'https://seekersguidance.org/articles/islamic-belief/the-meaning-of-the-shahada/' },
      { title: 'Taking the Shahada — WhyIslam', url: 'https://www.whyislam.org/new-muslims/taking-shahada/' },
      { title: 'Tawhid — The Oneness of Allah', url: 'https://yaqeeninstitute.org/topics/theology' },
    ],
  },
  {
    num: 2,
    arabic: 'الصَّلَاة',
    name: 'Salah',
    subtitle: 'The Five Daily Prayers',
    nameOfAllah: 'AS-SAMI',
    arabicName: 'السَّمِيع',
    meaning: 'The All-Hearing',
    ayat: 'وَأَقِيمُوا الصَّلَاةَ وَآتُوا الزَّكَاةَ وَارْكَعُوا مَعَ الرَّاكِعِينَ',
    ayatRef: 'Quran 2:43',
    ayatTrans: '"Establish prayer, give zakah, and bow with those who bow."',
    description: 'Salah — the five daily prayers — is the second pillar of Islam and the most consistent act of worship in a Muslim\'s life. Prayed at Fajr (dawn), Dhuhr (midday), Asr (afternoon), Maghrib (sunset), and Isha (night), each prayer is a direct conversation between the servant and the Lord. The Prophet ﷺ said: "The first matter that the slave will be brought to account for on the Day of Judgment is the prayer." At Masjid Al-Ihsan: Fajr 5:45am · Dhuhr 1:30pm · Asr 4:45pm · Maghrib Sunset · Isha 9:00pm.',
    resources: [
      { title: 'How to Pray — Step by Step', panelId: 'how-to-pray-steps' },
      { title: 'How to Pray — SeekersGuidance Video', panelId: 'how-to-pray-seekers' },
      { title: 'Salah — Yaqeen Institute', panelId: 'salah-yaqeen' },
    ],
  },
  {
    num: 3,
    arabic: 'الزَّكَاة',
    name: 'Zakat',
    subtitle: 'Obligatory Almsgiving',
    nameOfAllah: 'AL-KARIM',
    arabicName: 'الْكَرِيم',
    meaning: 'The Most Generous',
    ayat: 'وَأَقِيمُوا الصَّلَاةَ وَآتُوا الزَّكَاةَ وَمَا تُقَدِّمُوا لِأَنفُسِكُم مِّنْ خَيْرٍ تَجِدُوهُ عِندَ اللَّهِ',
    ayatRef: 'Quran 2:110',
    ayatTrans: '"Establish prayer and give zakah, and whatever good you put forward for yourselves — you will find it with Allah."',
    description: 'Zakat is the third pillar of Islam — obligatory annual charity given on qualifying wealth. It is 2.5% of savings held for one lunar year above the nisab (minimum threshold). Zakat is not charity in the conventional sense — it is the right of the poor in the wealth of the rich. It purifies wealth, circulates provision through the community, and is one of the most powerful mechanisms of economic justice in Islamic civilization. If you are in need, you may be eligible to receive Zakat. Contact Masjid Al-Ihsan.',
    resources: [
      { title: 'Zakat Calculator — NZF', url: 'https://nzfusa.org/zakat-calculator/' },
      { title: 'Zakat Application — ICNA Relief', url: 'https://icnarelief.org/zakat/' },
      { title: 'Zakat at Masjid Al-Ihsan', url: '/subhaal/masjid-al-ihsan/contact' },
    ],
  },
  {
    num: 4,
    arabic: 'الصَّوْم',
    name: 'Sawm',
    subtitle: 'Fasting in Ramadan',
    nameOfAllah: 'AS-SABUR',
    arabicName: 'الصَّبُور',
    meaning: 'The Patient',
    ayat: 'يَا أَيُّهَا الَّذِينَ آمَنُوا كُتِبَ عَلَيْكُمُ الصِّيَامُ كَمَا كُتِبَ عَلَى الَّذِينَ مِن قَبْلِكُمْ لَعَلَّكُمْ تَتَّقُونَ',
    ayatRef: 'Quran 21:107',
    ayatTrans: '"O you who have believed, decreed upon you is fasting as it was decreed upon those before you that you may become righteous."',
    description: 'Sawm — fasting during the month of Ramadan — is the fourth pillar of Islam. Muslims abstain from food, drink, and intimate relations from Fajr to Maghrib throughout the entire lunar month of Ramadan. Fasting is not merely physical abstention — it is a complete spiritual discipline that cultivates taqwa (God-consciousness), gratitude, empathy for the poor, and self-mastery. The Prophet ﷺ said: "Whoever fasts Ramadan out of faith and seeking reward, his previous sins will be forgiven." Masjid Al-Ihsan hosts Iftar on Wheels during Ramadan.',
    resources: [
      { title: 'Ramadan Guide — SeekersGuidance', url: 'https://seekersguidance.org/articles/fiqh/ramadan-guide/' },
      { title: 'Iftar on Wheels — Masjid Al-Ihsan', url: '/subhaal/masjid-al-ihsan/services' },
      { title: 'Fasting — Yaqeen Institute', url: 'https://yaqeeninstitute.org/topics/ramadan' },
    ],
  },
  {
    num: 5,
    arabic: 'الْحَج',
    name: 'Hajj',
    subtitle: 'Pilgrimage to Mecca',
    nameOfAllah: 'AL-JAMI',
    arabicName: 'الْجَامِع',
    meaning: 'The Gatherer',
    ayat: 'وَلِلَّهِ عَلَى النَّاسِ حِجُّ الْبَيْتِ مَنِ اسْتَطَاعَ إِلَيْهِ سَبِيلًا',
    ayatRef: 'Quran 3:97',
    ayatTrans: '"And [due] to Allah from the people is a pilgrimage to the House — for whoever is able to find thereto a way."',
    description: 'Hajj — the annual pilgrimage to Mecca — is the fifth pillar of Islam, obligatory once in a lifetime for every Muslim who is physically and financially able. Performed in the month of Dhul Hijjah, Hajj is the largest annual gathering of human beings on earth — a living symbol of the unity of the Muslim ummah before Allah. The rituals of Hajj trace the footsteps of Ibrahim ﷺ, Hajar, and Ismail ﷺ. It is the ultimate return — the human being stripped of all distinctions, standing before the One in white ihram. Al-Jami — the Gatherer — gathers all of humanity in this single act of worship.',
    resources: [
      { title: 'Hajj Guide — SeekersGuidance', url: 'https://seekersguidance.org/articles/fiqh/hajj-guide/' },
      { title: 'Hajj — Yaqeen Institute', url: 'https://yaqeeninstitute.org/topics/hajj' },
      { title: 'Hajj Planning — ISNA', url: 'https://www.isna.net/hajj/' },
    ],
  },
];

export default function NewMuslimsSection() {
  const [openId, setOpenId] = useState<string | null>('learn-prayer');
  const [activePanelId, setActivePanelId] = useState<string | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);

  const toggle = (id: string) => setOpenId(prev => prev === id ? null : id);

  const openPanel = (panelId: string) => {
    setActivePanelId(panelId);
    setPanelVisible(true);
  };

  const closePanel = () => {
    setPanelVisible(false);
    setTimeout(() => setActivePanelId(null), 350);
  };

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Prevent body scroll when panel open
  useEffect(() => {
    if (panelVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [panelVisible]);

  const renderPanelContent = (panelId: string) => {
    switch (panelId) {
      case 'how-to-pray-steps':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AS-SAMI — السَّمِيع</div>
              <div className="nm-panel-allah-meaning">The All-Hearing</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">وَأَقِيمُوا الصَّلَاةَ وَآتُوا الزَّكَاةَ وَارْكَعُوا مَعَ الرَّاكِعِينَ</span>
              <p className="nm-panel-ayah-trans">"Establish prayer, give zakah, and bow with those who bow." — Quran 2:43</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Salah is the direct line between the servant and the Lord. As-Sami — the All-Hearing — receives your prayer before you complete it. For the new Muslim, learning to pray is not merely a ritual — it is learning the language of the soul. Every raka&apos;ah is a conversation. Every sujood is a return. The Haal Project framework understands that the first act of reconstruction — of the self, of the community, of the city — begins with the one who turns toward Allah in prayer. Fisabilillah.</p>
            </div>

            <div className="nm-panel-section-title">📹 How to Pray — Video Guide</div>
            <div className="nm-panel-video-wrap">
              <iframe
                src="https://www.youtube.com/embed/zalLv2NY98k"
                title="How to Pray — Step by Step"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="nm-panel-video"
              />
            </div>
            <p className="nm-panel-video-caption">How to Pray — Complete Step-by-Step Guide</p>

            <div className="nm-panel-section-title">📖 How to Make Wudu (Ritual Purification)</div>
            <div className="nm-panel-steps">
              <div className="nm-panel-step-intro">
                <p>Before prayer, the Muslim must perform Wudu — ritual purification. The Prophet ﷺ said: "Allah does not accept prayer without purification." Wudu is both a physical and spiritual act of cleansing that prepares the believer to stand before Allah.</p>
              </div>
              {[
                { num: 1, title: 'Make Niyyah (Intention)', desc: 'Make the intention in your heart to perform wudu for the sake of Allah. Say: "Bismillah" — In the name of Allah.' },
                { num: 2, title: 'Wash Both Hands', desc: 'Wash both hands up to and including the wrists three times, making sure water reaches between the fingers.' },
                { num: 3, title: 'Rinse the Mouth', desc: 'Take water into the mouth and rinse it thoroughly three times.' },
                { num: 4, title: 'Rinse the Nose', desc: 'Sniff water into the nose and blow it out three times.' },
                { num: 5, title: 'Wash the Face', desc: 'Wash the entire face three times — from the hairline to the chin, and from ear to ear.' },
                { num: 6, title: 'Wash the Arms', desc: 'Wash the right arm from fingertips to and including the elbow three times, then the left arm three times.' },
                { num: 7, title: 'Wipe the Head', desc: 'Wipe the entire head once with wet hands — from the forehead to the back of the head and back again.' },
                { num: 8, title: 'Wipe the Ears', desc: 'Wipe the inside and outside of both ears once with wet fingers.' },
                { num: 9, title: 'Wash the Feet', desc: 'Wash the right foot up to and including the ankle three times, making sure water reaches between the toes. Then the left foot three times.' },
              ].map(step => (
                <div key={step.num} className="nm-panel-step">
                  <div className="nm-panel-step-num">{step.num}</div>
                  <div className="nm-panel-step-content">
                    <div className="nm-panel-step-title">{step.title}</div>
                    <div className="nm-panel-step-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
              <div className="nm-panel-step-note">
                <strong>What breaks Wudu:</strong> Using the toilet, passing gas, deep sleep, bleeding, vomiting, or losing consciousness. If any of these occur, wudu must be renewed before prayer.
              </div>
            </div>

            <div className="nm-panel-section-title">📖 How to Pray — Step by Step</div>
            <div className="nm-panel-steps">
              <div className="nm-panel-step-intro">
                <p>The five daily prayers are: Fajr (2 raka&apos;at), Dhuhr (4 raka&apos;at), Asr (4 raka&apos;at), Maghrib (3 raka&apos;at), and Isha (4 raka&apos;at). Each prayer follows the same basic structure.</p>
              </div>
              {[
                { num: 1, title: 'Face the Qibla', desc: 'Stand facing the direction of the Ka\'bah in Mecca (Qibla). In Chicago, this is approximately northeast. Use a Qibla compass or app.' },
                { num: 2, title: 'Make Niyyah (Intention)', desc: 'Make the intention in your heart for the specific prayer you are about to perform. The intention does not need to be spoken aloud.' },
                { num: 3, title: 'Takbiratul Ihram — Opening Takbir', desc: 'Raise both hands to shoulder level and say: "Allahu Akbar" (Allah is the Greatest). This begins the prayer.' },
                { num: 4, title: 'Qiyam — Standing', desc: 'Stand with hands folded over the chest (right hand over left). Recite Surah Al-Fatiha, then any additional surah or verses.' },
                { num: 5, title: 'Ruku — Bowing', desc: 'Say "Allahu Akbar" and bow with hands on knees, back parallel to the ground. Say: "Subhana Rabbiyal Azim" (Glory be to my Lord, the Most Great) three times.' },
                { num: 6, title: 'I\'tidal — Rising from Ruku', desc: 'Rise saying: "Sami Allahu liman hamidah" (Allah hears those who praise Him). Then say: "Rabbana lakal hamd" (Our Lord, to You is all praise).' },
                { num: 7, title: 'Sujood — Prostration', desc: 'Say "Allahu Akbar" and prostrate with forehead, nose, both palms, both knees, and toes touching the ground. Say: "Subhana Rabbiyal A\'la" (Glory be to my Lord, the Most High) three times.' },
                { num: 8, title: 'Julus — Sitting Between Prostrations', desc: 'Rise from sujood saying "Allahu Akbar" and sit briefly. Then perform the second sujood.' },
                { num: 9, title: 'Tashahhud — Final Sitting', desc: 'After completing all raka\'at, sit and recite the Tashahhud: "At-tahiyyatu lillahi was-salawatu wat-tayyibat..." and the Salawat (Durood Ibrahim).' },
                { num: 10, title: 'Tasleem — Ending the Prayer', desc: 'Turn the head to the right and say: "As-salamu alaykum wa rahmatullah." Then turn to the left and repeat. This ends the prayer.' },
              ].map(step => (
                <div key={step.num} className="nm-panel-step">
                  <div className="nm-panel-step-num">{step.num}</div>
                  <div className="nm-panel-step-content">
                    <div className="nm-panel-step-title">{step.title}</div>
                    <div className="nm-panel-step-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="nm-panel-cta">
              <p>Come pray with us at Masjid Al-Ihsan — 321 E Pershing Rd, Bronzeville, Chicago. All are welcome.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-cta-btn">Contact the Masjid →</a>
            </div>
          </div>
        );

      case 'how-to-pray-seekers':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AL-HADI — الْهَادِي</div>
              <div className="nm-panel-allah-meaning">The Guide</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">وَإِنَّكَ لَتَهْدِي إِلَىٰ صِرَاطٍ مُّسْتَقِيمٍ</span>
              <p className="nm-panel-ayah-trans">"And indeed, you guide to a straight path." — Quran 42:52</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Al-Hadi — the Guide — has provided us with teachers, scholars, and a living tradition of Islamic knowledge. SeekersGuidance is one of the premier online Islamic learning platforms in the world, offering free courses taught by qualified scholars. Their prayer series covers the fiqh of prayer in depth — from the conditions and pillars of prayer to the sunnahs and etiquettes. The instruction follows the Hanafi and Shafi&apos;i madhabs and is appropriate for new Muslims and those seeking to deepen their practice. Fisabilillah.</p>
            </div>

            <div className="nm-panel-section-title">📹 How to Pray — SeekersGuidance</div>
            <div className="nm-panel-video-wrap">
              <iframe
                src="https://www.youtube.com/embed/vx1rz-28HNk"
                title="How to Pray — SeekersGuidance"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="nm-panel-video"
              />
            </div>
            <p className="nm-panel-video-caption">How to Pray — SeekersGuidance Scholars Series</p>

            <div className="nm-panel-info-block">
              <div className="nm-panel-info-title">About SeekersGuidance</div>
              <p>SeekersGuidance is a global Islamic online learning platform offering free courses taught by qualified scholars. Their prayer series explores the spiritual dimensions of prayer — the inner states, the meanings of the words, the wisdom behind the movements, and the transformative potential of a consistent prayer practice. This is essential viewing for any Muslim seeking to deepen their relationship with prayer beyond the mechanics.</p>
              <a href="https://seekersguidance.org/courses/hanafi-fiqh-of-worship/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">
                Explore SeekersGuidance Courses →
              </a>
            </div>

            <div className="nm-panel-cta">
              <p>Have questions about prayer? Imam Madyun and the Masjid Al-Ihsan community are here to guide you.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-cta-btn">Contact the Masjid →</a>
            </div>
          </div>
        );

      case 'salah-yaqeen':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AL-WALI — الْوَلِيّ</div>
              <div className="nm-panel-allah-meaning">The Protecting Friend, The Ally</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">اللَّهُ وَلِيُّ الَّذِينَ آمَنُوا يُخْرِجُهُم مِّنَ الظُّلُمَاتِ إِلَى النُّورِ</span>
              <p className="nm-panel-ayah-trans">"Allah is the ally of those who believe. He brings them out from darknesses into the light." — Quran 2:257</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Al-Wali — the Protecting Friend — is the One who draws the believer from darkness into light through the act of prayer. The Yaqeen Institute has produced some of the most accessible and research-grounded content on the spiritual dimensions of prayer in the English language. For the new Muslim navigating a world that often pulls against the deen, understanding why we pray — not just how — is essential for building a sustainable and meaningful practice. Fisabilillah.</p>
            </div>

            <div className="nm-panel-section-title">📹 Salah — Yaqeen Institute Playlist</div>
            <div className="nm-panel-video-wrap">
              <iframe
                src="https://www.youtube.com/embed/LSayRo-KRKc?list=PLQ02IYL5pmhFgND4Tyy5cTYenOKK_QFj5"
                title="Salah — Yaqeen Institute"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="nm-panel-video"
              />
            </div>
            <p className="nm-panel-video-caption">Salah Series — Yaqeen Institute for Islamic Research</p>

            <div className="nm-panel-info-block">
              <div className="nm-panel-info-title">About Yaqeen Institute</div>
              <p>Yaqeen Institute for Islamic Research produces evidence-based content that addresses the spiritual, intellectual, and social needs of Muslims in the West. Their Salah series explores the spiritual dimensions of prayer — the inner states, the meanings of the words, the wisdom behind the movements, and the transformative potential of a consistent prayer practice. This is essential viewing for any Muslim seeking to deepen their relationship with prayer beyond the mechanics.</p>
              <a href="https://yaqeeninstitute.org/topics/prayer" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">
                Explore Yaqeen Institute →
              </a>
            </div>

            <div className="nm-panel-cta">
              <p>Join us for daily prayers at Masjid Al-Ihsan — 321 E Pershing Rd, Bronzeville, Chicago.</p>
              <a href="#prayer-times" className="nm-panel-cta-btn">View Prayer Times →</a>
            </div>
          </div>
        );

      case 'five-pillars-detail':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AL-MATIN — الْمَتِين</div>
              <div className="nm-panel-allah-meaning">The Firm, The Steadfast</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">إِنَّ اللَّهَ هُوَ الرَّزَّاقُ ذُو الْقُوَّةِ الْمَتِين</span>
              <p className="nm-panel-ayah-trans">"Indeed, it is Allah who is the Provider, the firm possessor of strength." — Quran 51:58</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Al-Matin — the Firm and Steadfast — has given the Muslim ummah a structure that holds through every storm. The Five Pillars are not a checklist — they are a complete architecture of the human being&apos;s relationship with Allah. Shahada is the foundation. Salah is the pillar. Zakat is the circulation. Sawm is the purification. Hajj is the return. The Haal Project understands that the reconstruction of the self and the community begins with these five acts — each one a complete orientation of the human being toward the One. Fisabilillah.</p>
            </div>

            {FIVE_PILLARS.map(pillar => (
              <div key={pillar.num} className="nm-panel-pillar">
                <div className="nm-panel-pillar-header">
                  <div className="nm-panel-pillar-num">{pillar.num}</div>
                  <div className="nm-panel-pillar-info">
                    <div className="nm-panel-pillar-arabic">{pillar.arabic}</div>
                    <div className="nm-panel-pillar-name">{pillar.name} — {pillar.subtitle}</div>
                    <div className="nm-panel-pillar-allah">{pillar.nameOfAllah} — {pillar.meaning}</div>
                  </div>
                </div>
                <div className="nm-panel-pillar-ayah">
                  <span className="nm-panel-pillar-ayah-arabic">{pillar.ayat}</span>
                  <span className="nm-panel-pillar-ayah-trans">{pillar.ayatTrans} — {pillar.ayatRef}</span>
                </div>
                <p className="nm-panel-pillar-desc">{pillar.description}</p>
                <div className="nm-panel-pillar-resources">
                  {pillar.resources.map((r, i) => (
                    r.panelId ? (
                      <button key={i} onClick={() => setActivePanelId(r.panelId!)} className="nm-panel-pillar-resource-btn">
                        {r.title} →
                      </button>
                    ) : (
                      <a key={i} href={r.url} target={r.url?.startsWith('http') ? '_blank' : '_self'} rel={r.url?.startsWith('http') ? 'noopener noreferrer' : undefined} className="nm-panel-pillar-resource-link">
                        {r.title} →
                      </a>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case 'ninety-nine-names':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Al-Asma al-Husna</div>
              <div className="nm-panel-allah-name">THE 99 NAMES OF ALLAH</div>
              <div className="nm-panel-allah-meaning">أَسْمَاءُ اللَّهِ الْحُسْنَى</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">وَلِلَّهِ الْأَسْمَاءُ الْحُسْنَىٰ فَادْعُوهُ بِهَا</span>
              <p className="nm-panel-ayah-trans">"And to Allah belong the best names, so invoke Him by them." — Quran 7:180</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>The 99 Names of Allah — Al-Asma al-Husna — are the foundation of Islamic theology and the lens through which the Haal Project understands the world. Each Name is a complete attribute of the Divine, a way of knowing Allah, and a way of orienting the self toward Him. The Prophet ﷺ said: "Allah has ninety-nine names, one hundred minus one. Whoever memorizes them will enter Paradise." Learning the Names is not merely an act of memorization — it is an act of knowing the One you worship. Fisabilillah.</p>
            </div>
            <div className="nm-panel-section-title">The 99 Beautiful Names of Allah</div>
            <div className="nm-panel-names-grid">
              {NAMES_OF_ALLAH.map(name => (
                <div key={name.num} className="nm-panel-name-card">
                  <div className="nm-panel-name-num">{name.num}</div>
                  <div className="nm-panel-name-arabic">{name.arabic}</div>
                  <div className="nm-panel-name-trans">{name.transliteration}</div>
                  <div className="nm-panel-name-meaning">{name.meaning}</div>
                </div>
              ))}
            </div>
            <div className="nm-panel-info-block" style={{ marginTop: 24 }}>
              <div className="nm-panel-info-title">Deeper Study</div>
              <p>The 99 Names are the foundation of the Haal Project&apos;s Caretaker Mata framework — each ward, each resource category, each act of community service is mapped to a Name of Allah. To know the Names is to understand the framework through which this community serves. For deeper study, visit myislam.org/99-names-of-allah for detailed explanations of each Name.</p>
              <a href="https://myislam.org/99-names-of-allah/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">
                Explore 99 Names — MyIslam.org →
              </a>
            </div>
          </div>
        );

      case 'six-pillars-iman':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AL-HAQQ — الْحَقّ</div>
              <div className="nm-panel-allah-meaning">The Absolute Truth</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">آمَنَ الرَّسُولُ بِمَا أُنزِلَ إِلَيْهِ مِن رَّبِّهِ وَالْمُؤْمِنُونَ ۚ كُلٌّ آمَنَ بِاللَّهِ وَمَلَائِكَتِهِ وَكُتُبِهِ وَرُسُلِهِ</span>
              <p className="nm-panel-ayah-trans">"The Messenger has believed in what was revealed to him from his Lord, and so have the believers. All of them have believed in Allah and His angels and His books and His messengers." — Quran 2:285</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Al-Haqq — the Absolute Truth — is the foundation of Iman. Belief is not merely intellectual assent; it is the orientation of the entire being toward what is real. The six pillars of Iman map the full landscape of the unseen: Allah, His angels, His books, His messengers, the Last Day, and divine decree. For the new Muslim, building this inner architecture is the work of a lifetime — and it begins with the first sincere utterance of the Shahada. Fisabilillah.</p>
            </div>

            {[
              {
                num: 1, arabic: 'الإِيمَان بِاللَّه', name: 'Belief in Allah',
                nameOfAllah: 'AL-AHAD', arabicName: 'الأَحَد', meaning: 'The Unique, The One',
                ayat: 'قُلْ هُوَ اللَّهُ أَحَدٌ', ayatRef: 'Quran 112:1',
                ayatTrans: '"Say: He is Allah, the One."',
                desc: 'The first and most fundamental pillar of Iman is belief in Allah — His existence, His oneness (Tawhid), His names and attributes, and His right to be worshipped alone. Tawhid is the foundation of the entire Islamic worldview. It means that Allah alone is the Creator, the Sustainer, the Sovereign, and the only One deserving of worship. Every other pillar of Iman flows from this first belief.',
                resources: [
                  { title: 'Tawhid — Yaqeen Institute', url: 'https://yaqeeninstitute.org/topics/theology' },
                  { title: '99 Names of Allah', url: 'https://myislam.org/99-names-of-allah/' },
                ],
              },
              {
                num: 2, arabic: 'الإِيمَان بِالْمَلَائِكَة', name: 'Belief in Angels',
                nameOfAllah: 'AL-AZIZ', arabicName: 'الْعَزِيز', meaning: 'The Almighty',
                ayat: 'وَلِلَّهِ يَسْجُدُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ مِن دَابَّةٍ وَالْمَلَائِكَةُ', ayatRef: 'Quran 16:49',
                ayatTrans: '"And to Allah prostrates whatever is in the heavens and whatever is on the earth of creatures, and the angels."',
                desc: 'Muslims believe in the existence of angels — beings created from light who worship Allah continuously and carry out His commands. Among the most well-known angels are Jibreel (Gabriel), who brought revelation to the prophets; Mikail, who oversees provision and rain; Israfil, who will blow the trumpet on the Day of Judgment; and Azrael, the angel of death. Two angels — Kiraman Katibin — record every deed of every human being.',
                resources: [
                  { title: 'Angels in Islam — SeekersGuidance', url: 'https://seekersguidance.org/articles/islamic-belief/angels-in-islam/' },
                ],
              },
              {
                num: 3, arabic: 'الإِيمَان بِالْكُتُب', name: 'Belief in Divine Books',
                nameOfAllah: 'AL-ALIM', arabicName: 'الْعَلِيم', meaning: 'The All-Knowing',
                ayat: 'اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ', ayatRef: 'Quran 96:1',
                ayatTrans: '"Read in the name of your Lord who created."',
                desc: 'Muslims believe that Allah revealed divine books to His prophets as guidance for humanity. These include the Suhuf (scrolls) of Ibrahim, the Tawrah (Torah) of Musa, the Zabur (Psalms) of Dawud, the Injil (Gospel) of Isa, and the Quran revealed to Muhammad ﷺ. The Quran is the final, preserved, and complete revelation — unchanged from the time of its revelation to the present day. It is the primary source of Islamic guidance.',
                resources: [
                  { title: 'Quran.com — Read & Listen', url: 'https://quran.com/' },
                  { title: 'The Quran — Yaqeen Institute', url: 'https://yaqeeninstitute.org/topics/quran' },
                ],
              },
              {
                num: 4, arabic: 'الإِيمَان بِالرُّسُل', name: 'Belief in Prophets & Messengers',
                nameOfAllah: 'AL-HADI', arabicName: 'الْهَادِي', meaning: 'The Guide',
                ayat: 'وَمَا أَرْسَلْنَاكَ إِلَّا رَحْمَةً لِّلْعَالَمِينَ', ayatRef: 'Quran 21:107',
                ayatTrans: '"And We have not sent you except as a mercy to the worlds."',
                desc: 'Muslims believe that Allah sent prophets and messengers to every nation throughout history to guide humanity to the truth. Among them are Adam, Nuh (Noah), Ibrahim (Abraham), Musa (Moses), Isa (Jesus), and Muhammad ﷺ — the final prophet and messenger. Muslims believe in all the prophets without distinction. The Prophet Muhammad ﷺ is the Seal of the Prophets — the final messenger sent to all of humanity until the Day of Judgment.',
                resources: [
                  { title: 'The Prophet Muhammad ﷺ — SeekersGuidance', url: 'https://seekersguidance.org/articles/prophet-muhammad/' },
                  { title: 'Seerah — Yaqeen Institute', url: 'https://yaqeeninstitute.org/topics/seerah' },
                ],
              },
              {
                num: 5, arabic: 'الإِيمَان بِالْيَوْم الآخِر', name: 'Belief in the Last Day',
                nameOfAllah: 'AL-HAYY', arabicName: 'الْحَيّ', meaning: 'The Ever-Living',
                ayat: 'كُلُّ نَفْسٍ ذَائِقَةُ الْمَوْتِ ۗ وَإِنَّمَا تُوَفَّوْنَ أُجُورَكُمْ يَوْمَ الْقِيَامَةِ', ayatRef: 'Quran 3:185',
                ayatTrans: '"Every soul will taste death, and you will only be given your full compensation on the Day of Resurrection."',
                desc: 'Belief in the Last Day — the Day of Judgment (Yawm al-Qiyamah) — gives life its ultimate meaning and orients every action toward the eternal. This belief encompasses: death and the life of the grave (Barzakh), the resurrection, the gathering, the reckoning, the scales (Mizan), the bridge (Sirat), Paradise (Jannah), and Hellfire (Jahannam).',
                resources: [
                  { title: 'The Afterlife — Yaqeen Institute', url: 'https://yaqeeninstitute.org/topics/afterlife' },
                  { title: 'Death & the Afterlife — SeekersGuidance', url: 'https://seekersguidance.org/articles/islamic-belief/death-afterlife/' },
                ],
              },
              {
                num: 6, arabic: 'الإِيمَان بِالْقَدَر', name: 'Belief in Divine Decree (Qadar)',
                nameOfAllah: 'AL-QADIR', arabicName: 'الْقَادِر', meaning: 'The Capable, The All-Powerful',
                ayat: 'إِنَّا كُلَّ شَيْءٍ خَلَقْنَاهُ بِقَدَرٍ', ayatRef: 'Quran 54:49',
                ayatTrans: '"Indeed, all things We created with predestination."',
                desc: 'Belief in Qadar — divine decree — brings peace, gratitude in blessings, and patience in trials. The Prophet ﷺ said: "Know that what has befallen you was not going to miss you, and what has missed you was not going to befall you."',
                resources: [
                  { title: 'Qadar — Divine Decree', url: 'https://yaqeeninstitute.org/read/paper/divine-decree-and-human-agency' },
                  { title: 'Understanding Qadar — SeekersGuidance', url: 'https://seekersguidance.org/articles/islamic-belief/qadar-divine-decree/' },
                ],
              },
            ].map(pillar => (
              <div key={pillar.num} className="nm-panel-pillar">
                <div className="nm-panel-pillar-header">
                  <div className="nm-panel-pillar-num">{pillar.num}</div>
                  <div className="nm-panel-pillar-info">
                    <div className="nm-panel-pillar-arabic">{pillar.arabic}</div>
                    <div className="nm-panel-pillar-name">{pillar.name}</div>
                    <div className="nm-panel-pillar-allah">{pillar.nameOfAllah} — {pillar.meaning}</div>
                  </div>
                </div>
                <div className="nm-panel-pillar-ayah">
                  <span className="nm-panel-pillar-ayah-arabic">{pillar.ayat}</span>
                  <span className="nm-panel-pillar-ayah-trans">{pillar.ayatTrans} — {pillar.ayatRef}</span>
                </div>
                <p className="nm-panel-pillar-desc">{pillar.desc}</p>
                <div className="nm-panel-pillar-resources">
                  {pillar.resources.map((r, i) => (
                    <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" className="nm-panel-pillar-resource-link">
                      {r.title} →
                    </a>
                  ))}
                </div>
              </div>
            ))}

            <div className="nm-panel-cta">
              <p>Questions about Iman and belief? Imam Madyun and the Masjid Al-Ihsan community are here to guide you.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-cta-btn">Contact the Masjid →</a>
            </div>
          </div>
        );

      case 'marriage-panel':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AL-WADUD — الْوَدُود</div>
              <div className="nm-panel-allah-meaning">The Loving, The Affectionate</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ بَيْنَكُم مَّوَدَّةً وَرَحْمَةً</span>
              <p className="nm-panel-ayah-trans">"And of His signs is that He created for you from yourselves mates that you may find tranquility in them; and He placed between you affection and mercy." — Quran 30:21</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Al-Wadud — the Loving — placed mawaddah and rahmah between spouses as a sign of His own nature. Marriage in Islam is not merely a social contract; it is a divine covenant (mithaq) that mirrors the relationship between the believer and Allah. The Prophet ﷺ said: &quot;Marriage is half of the deen.&quot; For the new Muslim, understanding the rights, responsibilities, and spiritual dimensions of Islamic marriage is essential for building a home that is a place of sakina — tranquility. Fisabilillah.</p>
            </div>

            <div className="nm-panel-section-title">📹 Marriage in Islam — Video Resource</div>
            <div className="nm-panel-video-wrap">
              <iframe
                src="https://www.youtube.com/embed/8a7awUunJ4s?list=PLEQX1fhjQx8NMMAebHEdQX1jJwviDCEi6"
                title="Marriage in Islam — Talks & Lectures"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="nm-panel-video"
              />
            </div>
            <p className="nm-panel-video-caption">Talks & Lectures — Masjid Al-Ihsan · Marriage & Family in Islam</p>

            <div className="nm-panel-steps">
              <div className="nm-panel-step-intro">
                <p>The Nikah (Islamic marriage contract) is a sacred covenant between two people before Allah. It requires: the consent of both parties, a wali (guardian) for the bride, two witnesses, and a mahr (dowry gift) from the groom to the bride. The Nikah can be performed at Masjid Al-Ihsan by Imam Madyun.</p>
              </div>
              {[
                { num: 1, title: 'The Mahr (Dowry)', desc: 'The mahr is a mandatory gift from the groom to the bride — it is her right and her property. It can be money, gold, property, or any agreed-upon gift. It must be specified in the Nikah contract.' },
                { num: 2, title: 'The Wali (Guardian)', desc: 'The bride\'s wali (guardian) — typically her father or male relative — must be present and give consent for the Nikah to be valid. If no wali is available, the imam can serve as wali.' },
                { num: 3, title: 'Two Witnesses', desc: 'Two adult Muslim witnesses must be present at the Nikah to validate the contract. They testify to the agreement between the two parties.' },
                { num: 4, title: 'The Ijab and Qabul', desc: 'The Nikah is completed through the offer (ijab) and acceptance (qabul) — the bride or her wali offers the marriage and the groom accepts. This exchange, witnessed by two Muslims, constitutes the Islamic marriage.' },
                { num: 5, title: 'Rights of the Husband', desc: 'The husband is responsible for providing for his wife financially (nafaqah) — food, clothing, shelter, and care. He is the qawwam (protector and maintainer) of the family, and must treat his wife with kindness and respect.' },
                { num: 6, title: 'Rights of the Wife', desc: 'The wife has the right to financial support, kind treatment, her mahr, and to be consulted in family matters. She retains her own property and financial independence. Her consent is required for the marriage to be valid.' },
                { num: 7, title: 'The Islamic Home (Bayt)', desc: 'The Islamic home is built on sakina (tranquility), mawaddah (affection), and rahmah (mercy). The Prophet ﷺ said: "The best of you are those who are best to their families, and I am the best of you to my family."' },
              ].map(step => (
                <div key={step.num} className="nm-panel-step">
                  <div className="nm-panel-step-num">{step.num}</div>
                  <div className="nm-panel-step-content">
                    <div className="nm-panel-step-title">{step.title}</div>
                    <div className="nm-panel-step-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="nm-panel-info-block">
              <div className="nm-panel-info-title">Nikah at Masjid Al-Ihsan</div>
              <p>Imam Abdullah Madyun performs Nikah ceremonies at Masjid Al-Ihsan. Contact the Masjid to arrange an Islamic marriage ceremony, pre-marital counseling, or to ask questions about Islamic marriage.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-ext-link">
                Contact Masjid Al-Ihsan →
              </a>
            </div>

            <div className="nm-panel-info-block">
              <div className="nm-panel-info-title">Additional Resources</div>
              <p>For deeper study on Islamic marriage, rights, and family life:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                <a href="https://seekersguidance.org/articles/family-and-gender/marriage/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">SeekersGuidance — Islamic Marriage Guide →</a>
                <a href="https://yaqeeninstitute.org/read/paper/marriage-in-islam" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">Yaqeen Institute — Marriage in Islam →</a>
                <a href="https://darulqasim.org/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">Darul Qasim Chicago — Pre-Marital Counseling →</a>
              </div>
            </div>

            <div className="nm-panel-cta">
              <p>Ready to take the next step? Contact Masjid Al-Ihsan to speak with Imam Madyun about Islamic marriage.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-cta-btn">Contact the Masjid →</a>
            </div>
          </div>
        );

      case 'janazah-panel':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AL-HAYY — الْحَيّ</div>
              <div className="nm-panel-allah-meaning">The Ever-Living</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">كُلُّ نَفْسٍ ذَائِقَةُ الْمَوْتِ ۗ وَإِنَّمَا تُوَفَّوْنَ أُجُورَكُمْ يَوْمَ الْقِيَامَةِ</span>
              <p className="nm-panel-ayah-trans">"Every soul will taste death, and you will only be given your full compensation on the Day of Resurrection." — Quran 3:185</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Al-Hayy — the Ever-Living — reminds us that death is not an ending but a transition. The Janazah prayer is a collective obligation (fard kifayah) upon the Muslim community — a final act of brotherhood and sisterhood for the one who has returned to Allah. Understanding Islamic funeral rites — from ghusl to burial — is an act of love for our community and preparation for our own return. Every Muslim community must be equipped to honor its dead. Fisabilillah.</p>
            </div>

            <div className="nm-panel-section-title">📹 Janazah — Video Resource</div>
            <div className="nm-panel-video-wrap">
              <iframe
                src="https://www.youtube.com/embed/8a7awUunJ4s?list=PLEQX1fhjQx8NMMAebHEdQX1jJwviDCEi6"
                title="Janazah — Islamic Funeral Rites"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="nm-panel-video"
              />
            </div>
            <p className="nm-panel-video-caption">Talks & Lectures — Masjid Al-Ihsan · Death, Janazah & the Afterlife</p>

            <div className="nm-panel-steps">
              <div className="nm-panel-step-intro">
                <p>The Islamic funeral rites (Janazah) are a collective obligation (fard kifayah) — if some members of the community fulfill them, the obligation is lifted from the rest. If no one fulfills them, the entire community is sinful. Every Muslim should know the basic rites.</p>
              </div>
              {[
                { num: 1, title: 'Ghusl — Ritual Washing of the Deceased', desc: 'The body of the deceased must be washed (ghusl) by Muslims of the same gender. The body is washed an odd number of times (3, 5, or 7) with water and soap, beginning with the right side. Camphor is used in the final washing.' },
                { num: 2, title: 'Kafan — Shrouding', desc: 'The body is wrapped in white cloth (kafan). For men, three pieces of white cloth. For women, five pieces. The kafan should be simple — a reminder that we leave this world as we entered it.' },
                { num: 3, title: 'Salat al-Janazah — The Funeral Prayer', desc: 'The Janazah prayer is performed in congregation. It consists of four takbeers (Allahu Akbar), recitation of Al-Fatiha, Salawat on the Prophet ﷺ, du\'a for the deceased, and tasleem. There is no ruku or sujood.' },
                { num: 4, title: 'The Janazah Prayer — How to Perform', desc: '1st Takbeer: Recite Al-Fatiha silently. 2nd Takbeer: Recite Salawat (Durood Ibrahim). 3rd Takbeer: Make du\'a for the deceased. 4th Takbeer: Make du\'a for the Muslim community. Then tasleem to the right.' },
                { num: 5, title: 'Burial — Al-Dafn', desc: 'The deceased is buried as soon as possible — ideally the same day. The body is placed in the grave on its right side, facing the Qibla (direction of Mecca). The grave is filled with earth. Elaborate grave markers are discouraged in Islam.' },
                { num: 6, title: 'Du\'a for the Deceased', desc: 'After burial, it is Sunnah to make du\'a for the deceased. The Prophet ﷺ would stand at the grave and say: "Ask forgiveness for your brother and pray for his steadfastness, for he is now being questioned." Continue to make du\'a for the deceased in your prayers.' },
                { num: 7, title: 'Condolences — Ta\'ziyah', desc: 'It is Sunnah to offer condolences (ta\'ziyah) to the family of the deceased within three days of the death. Say: "Inna lillahi wa inna ilayhi raji\'un" — "Indeed, to Allah we belong and to Him we shall return." (Quran 2:156)' },
              ].map(step => (
                <div key={step.num} className="nm-panel-step">
                  <div className="nm-panel-step-num">{step.num}</div>
                  <div className="nm-panel-step-content">
                    <div className="nm-panel-step-title">{step.title}</div>
                    <div className="nm-panel-step-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="nm-panel-info-block">
              <div className="nm-panel-info-title">Janazah Services — Masjid Al-Ihsan</div>
              <p>Masjid Al-Ihsan provides Janazah services for the Muslim community in Chicago. Contact Imam Madyun for assistance with funeral arrangements, ghusl, Janazah prayer, and burial coordination.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-ext-link">
                Contact Masjid Al-Ihsan →
              </a>
            </div>

            <div className="nm-panel-info-block">
              <div className="nm-panel-info-title">Additional Resources</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                <a href="https://islamqa.info/en/answers/36853/how-to-pray-the-funeral-prayer" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">IslamQA — How to Pray the Janazah →</a>
                <a href="https://seekersguidance.org/articles/fiqh/the-fiqh-of-janazah/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">SeekersGuidance — Complete Fiqh of Janazah →</a>
              </div>
            </div>

            <div className="nm-panel-cta">
              <p>Need Janazah services or have questions about Islamic funeral rites? Contact Masjid Al-Ihsan.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-cta-btn">Contact the Masjid →</a>
            </div>
          </div>
        );

      case 'aqidah-fiqh-panel':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AL-KHABIR — الْخَبِير</div>
              <div className="nm-panel-allah-meaning">The All-Aware</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">وَهُوَ اللَّطِيفُ الْخَبِيرُ</span>
              <p className="nm-panel-ayah-trans">"And He is the Subtle, the Acquainted." — Quran 6:103</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Al-Khabir — the All-Aware — knows the inner reality of every matter. Aqidah is the science of aligning our beliefs with what Allah has revealed about Himself and His creation. Fiqh is the practical application of divine guidance to every dimension of life. Together they form the complete Muslim — grounded in correct belief and guided in correct action. For the new Muslim, building a foundation in aqidah and fiqh is the work of a lifetime, and it begins with the first question asked in sincerity. Fisabilillah.</p>
            </div>

            <div className="nm-panel-section-title">📹 Aqidah & Fiqh — Video Resources</div>
            <div className="nm-panel-video-wrap">
              <iframe
                src="https://www.youtube.com/embed/8a7awUunJ4s?list=PLEQX1fhjQx8NMMAebHEdQX1jJwviDCEi6"
                title="Aqidah & Fiqh — Talks & Lectures"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="nm-panel-video"
              />
            </div>
            <p className="nm-panel-video-caption">Talks & Lectures — Masjid Al-Ihsan · Aqidah & Fiqh</p>

            <div className="nm-panel-steps">
              <div className="nm-panel-step-intro">
                <p>Aqidah (Islamic theology) and Fiqh (Islamic jurisprudence) are the two pillars of Islamic knowledge. Aqidah deals with what we believe; Fiqh deals with how we act. Both are essential for a grounded and practiced Muslim life.</p>
              </div>
              {[
                { num: 1, title: 'What is Aqidah?', desc: 'Aqidah is the science of Islamic belief — it covers the six pillars of Iman (belief in Allah, angels, books, messengers, the Last Day, and divine decree), the attributes of Allah, and the correct understanding of the unseen. Sound aqidah is the foundation of the Muslim\'s entire worldview.' },
                { num: 2, title: 'The Madhabs — Schools of Fiqh', desc: 'There are four main schools of Islamic jurisprudence (madhabs): Hanafi, Maliki, Shafi\'i, and Hanbali. All four are valid and represent the diversity of scholarly interpretation within the Sunni tradition. New Muslims are encouraged to follow one madhab consistently for stability in practice.' },
                { num: 3, title: 'Fiqh of Worship (Ibadat)', desc: 'The fiqh of worship covers the practical rulings for the five pillars: purification (taharah), prayer (salah), fasting (sawm), zakat, and hajj. These rulings govern how each act of worship is performed correctly according to the Quran and Sunnah.' },
                { num: 4, title: 'Fiqh of Daily Life (Mu\'amalat)', desc: 'Beyond worship, fiqh governs every dimension of daily life: business transactions (halal and haram), marriage and divorce, inheritance, food and drink, clothing, and social interactions. Islam is a complete way of life — fiqh is its practical expression.' },
                { num: 5, title: 'Tazkiyah — Purification of the Soul', desc: 'Alongside aqidah and fiqh is the science of tazkiyah — the purification of the heart and character. This includes the study of virtues (tawadu, sabr, shukr, tawakkul) and vices (kibr, hasad, riya) and the practical work of transforming the inner self. Tazkiyah is the inner dimension of the deen.' },
                { num: 6, title: 'Seeking Knowledge — Talab al-Ilm', desc: 'The Prophet ﷺ said: "Seeking knowledge is an obligation upon every Muslim." The journey of Islamic learning is a lifelong one. Begin with the basics — purification, prayer, and the fundamentals of aqidah — and build from there. Find a qualified teacher and a community of learners.' },
              ].map(step => (
                <div key={step.num} className="nm-panel-step">
                  <div className="nm-panel-step-num">{step.num}</div>
                  <div className="nm-panel-step-content">
                    <div className="nm-panel-step-title">{step.title}</div>
                    <div className="nm-panel-step-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="nm-panel-info-block">
              <div className="nm-panel-info-title">Learning Resources</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                <a href="https://seekersguidance.org/courses/hanafi-fiqh-of-worship/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">SeekersGuidance — Fiqh of Worship (Free Course) →</a>
                <a href="https://yaqeeninstitute.org/topics/theology" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">Yaqeen Institute — Aqidah Papers →</a>
                <a href="https://darulqasim.org/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">Darul Qasim Chicago — Islamic Studies →</a>
                <a href="https://seekersguidance.org/courses/purification-of-the-soul/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">SeekersGuidance — Purification of the Soul →</a>
              </div>
            </div>

            <div className="nm-panel-cta">
              <p>Questions about Islamic belief or practice? Imam Madyun is available for guidance at Masjid Al-Ihsan.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-cta-btn">Contact the Masjid →</a>
            </div>
          </div>
        );

      case 'quran-hadith-panel':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AL-ALIM — الْعَلِيم</div>
              <div className="nm-panel-allah-meaning">The All-Knowing</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ</span>
              <p className="nm-panel-ayah-trans">"Read in the name of your Lord who created." — Quran 96:1</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Al-Alim — the All-Knowing — revealed the Quran as a complete guidance for humanity. The first command of revelation was to read. The Quran is not merely a book to be recited — it is a living guidance to be understood, internalized, and embodied. The Hadith are the living commentary of the Prophet ﷺ on that guidance. For the new Muslim, beginning the journey of Quran and Hadith is the beginning of a lifelong conversation with Allah and His Messenger. Fisabilillah.</p>
            </div>

            <div className="nm-panel-section-title">📹 Quran & Hadith — Video Resources</div>
            <div className="nm-panel-video-wrap">
              <iframe
                src="https://www.youtube.com/embed/n4_T7NbH84c?list=PLEQX1fhjQx8Nal72X6Dr2ikYskuzbfboX"
                title="Quran & Hadith — Khutbah Series"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="nm-panel-video"
              />
            </div>
            <p className="nm-panel-video-caption">Khutbah Series — Masjid Al-Ihsan · Quran & Hadith</p>

            <div className="nm-panel-steps">
              <div className="nm-panel-step-intro">
                <p>The Quran and Hadith are the two primary sources of Islamic guidance. Every Muslim should develop a relationship with both — beginning with the basics and building over a lifetime of study.</p>
              </div>
              {[
                { num: 1, title: 'Beginning with Al-Fatiha', desc: 'The first surah of the Quran — Al-Fatiha (The Opening) — is the most recited text in Islamic worship. It is recited in every raka\'ah of every prayer. Memorizing and understanding Al-Fatiha is the first step in the Quranic journey. It is a complete du\'a — a prayer for guidance to the straight path.' },
                { num: 2, title: 'Learning to Read Arabic', desc: 'The Quran was revealed in Arabic and is recited in Arabic in prayer. Learning to read Arabic script — even at a basic level — opens the door to a direct relationship with the Quran. Begin with the Arabic alphabet and basic Tajweed (rules of Quranic recitation). Many masajid offer free Quran reading classes.' },
                { num: 3, title: 'Quran with Translation', desc: 'While learning Arabic, read the Quran with a reliable English translation. Recommended translations include: The Clear Quran (Dr. Mustafa Khattab), The Study Quran (Seyyed Hossein Nasr), and Sahih International. Read with reflection (tadabbur) — not just for information but for transformation.' },
                { num: 4, title: 'The 40 Hadith of Imam Nawawi', desc: 'The 40 Hadith Nawawi is the most essential collection of hadith for new Muslims — 42 hadith that cover the foundations of the deen. Begin here before moving to the larger collections. Available free at sunnah.com/nawawi40.' },
                { num: 5, title: 'The Six Major Hadith Collections', desc: 'The six major collections of authentic hadith (Kutub al-Sittah) are: Sahih al-Bukhari, Sahih Muslim, Sunan Abu Dawud, Jami\' al-Tirmidhi, Sunan al-Nasa\'i, and Sunan Ibn Majah. All are available free at sunnah.com. Begin with Bukhari and Muslim for the most rigorously authenticated hadith.' },
                { num: 6, title: 'Quran Halaqa at Masjid Al-Ihsan', desc: 'Masjid Al-Ihsan holds ongoing Quran Halaqas (study circles) after Isha prayer. All levels are welcome — from beginners learning to read to advanced students studying Tafsir. The halaqa is a space of learning, reflection, and community. Contact the Masjid for the current schedule.' },
                { num: 7, title: 'Tafsir — Quranic Exegesis', desc: 'Tafsir is the science of Quranic interpretation — understanding the context, meaning, and application of each verse. Recommended tafsir resources for English speakers include: Tafsir Ibn Kathir (classical), In the Shade of the Quran by Sayyid Qutb, and the Bayyinah TV platform by Nouman Ali Khan.' },
              ].map(step => (
                <div key={step.num} className="nm-panel-step">
                  <div className="nm-panel-step-num">{step.num}</div>
                  <div className="nm-panel-step-content">
                    <div className="nm-panel-step-title">{step.title}</div>
                    <div className="nm-panel-step-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="nm-panel-info-block">
              <div className="nm-panel-info-title">Essential Online Resources</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                <a href="https://quran.com/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">Quran.com — Read, Listen & Study →</a>
                <a href="https://sunnah.com/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">Sunnah.com — Hadith Collections →</a>
                <a href="https://sunnah.com/nawawi40" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">40 Hadith Nawawi — Essential Collection →</a>
                <a href="https://www.bayyinahtv.com/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">Bayyinah TV — Quran Study Platform →</a>
              </div>
            </div>

            <div className="nm-panel-cta">
              <p>Join the Quran Halaqa at Masjid Al-Ihsan — all levels welcome. Contact us for the current schedule.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-cta-btn">Contact the Masjid →</a>
            </div>
          </div>
        );

      case 'zakat-panel':
        return (
          <div className="nm-panel-body">
            <div className="nm-panel-allah-header">
              <div className="nm-panel-allah-label">Name of Allah</div>
              <div className="nm-panel-allah-name">AL-KARIM — الْكَرِيم</div>
              <div className="nm-panel-allah-meaning">The Most Generous</div>
            </div>
            <div className="nm-panel-ayah">
              <span className="nm-panel-ayah-arabic">وَأَقِيمُوا الصَّلَاةَ وَآتُوا الزَّكَاةَ وَمَا تُقَدِّمُوا لِأَنفُسِكُم مِّنْ خَيْرٍ تَجِدُوهُ عِندَ اللَّهِ</span>
              <p className="nm-panel-ayah-trans">"Establish prayer and give zakah, and whatever good you put forward for yourselves — you will find it with Allah." — Quran 2:110</p>
            </div>
            <div className="nm-panel-fisabilillah">
              <div className="nm-panel-fisabilillah-label">Fisabilillah</div>
              <p>Al-Karim — the Most Generous — has made generosity a pillar of the deen. Zakat is not charity in the conventional sense — it is the purification of wealth, the right of the poor in the property of the rich, and the circulation of divine provision through the community. For those in need, Zakat is a right — not a handout. For those with qualifying wealth, it is an obligation — not a choice. Fisabilillah.</p>
            </div>

            <div className="nm-panel-section-title">📹 Zakat — Video Resource</div>
            <div className="nm-panel-video-wrap">
              <iframe
                src="https://www.youtube.com/embed/8a7awUunJ4s?list=PLEQX1fhjQx8NMMAebHEdQX1jJwviDCEi6"
                title="Zakat — Talks & Lectures"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="nm-panel-video"
              />
            </div>
            <p className="nm-panel-video-caption">Talks & Lectures — Masjid Al-Ihsan · Zakat & Sadaqah</p>

            <div className="nm-panel-steps">
              <div className="nm-panel-step-intro">
                <p>Zakat is the third pillar of Islam — obligatory annual charity given on qualifying wealth. It is 2.5% of savings held for one full lunar year above the nisab (minimum threshold). Understanding Zakat — both giving and receiving — is essential for every Muslim.</p>
              </div>
              {[
                { num: 1, title: 'What is Zakat?', desc: 'Zakat is the obligatory annual purification of wealth. It is 2.5% of all savings, gold, silver, and business inventory held for one full lunar year above the nisab (minimum threshold). Zakat is one of the five pillars of Islam — it is not optional for those who qualify.' },
                { num: 2, title: 'The Nisab — Minimum Threshold', desc: 'The nisab is the minimum amount of wealth one must possess before Zakat becomes obligatory. It is equivalent to 87.48 grams of gold or 612.36 grams of silver. If your savings exceed the nisab for one full lunar year, you must pay Zakat on the total amount.' },
                { num: 3, title: 'Who Must Pay Zakat?', desc: 'Zakat is obligatory on every adult Muslim who: (1) possesses wealth above the nisab, (2) has held that wealth for one full lunar year (hawl), and (3) is free from debt that would reduce their wealth below the nisab. Zakat is not required on wealth used for daily needs.' },
                { num: 4, title: 'The Eight Recipients of Zakat', desc: 'Allah has specified eight categories of people who may receive Zakat (Quran 9:60): the poor (fuqara), the needy (masakin), Zakat administrators, those whose hearts are to be reconciled, those in bondage, those in debt, those in the way of Allah (fi sabilillah), and the wayfarer (ibn al-sabil).' },
                { num: 5, title: 'Zakat al-Fitr', desc: 'Zakat al-Fitr is a special obligatory charity given at the end of Ramadan before Eid al-Fitr prayer. It is approximately 2.5 kg of staple food (or its cash equivalent) per person in the household. It must be given before the Eid prayer to be valid.' },
                { num: 6, title: 'Sadaqah — Voluntary Charity', desc: 'Beyond Zakat, Islam strongly encourages Sadaqah — voluntary charity. The Prophet ﷺ said: "Sadaqah extinguishes sin as water extinguishes fire." Sadaqah can be given at any time, in any amount, to any worthy cause. Even a smile is Sadaqah.' },
                { num: 7, title: 'Applying for Zakat Assistance', desc: 'If you are in need and believe you may qualify to receive Zakat, you have a right to ask. Contact Masjid Al-Ihsan or ICNA Relief to apply for Zakat assistance. There is no shame in receiving what Allah has made your right.' },
              ].map(step => (
                <div key={step.num} className="nm-panel-step">
                  <div className="nm-panel-step-num">{step.num}</div>
                  <div className="nm-panel-step-content">
                    <div className="nm-panel-step-title">{step.title}</div>
                    <div className="nm-panel-step-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="nm-panel-info-block">
              <div className="nm-panel-info-title">Zakat Resources</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                <a href="https://nzfusa.org/zakat-calculator/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">NZF Zakat Calculator — Calculate Your Obligation →</a>
                <a href="https://icnarelief.org/zakat/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">ICNA Relief — Apply for Zakat Assistance →</a>
                <a href="https://seekersguidance.org/articles/fiqh/zakat-complete-guide/" target="_blank" rel="noopener noreferrer" className="nm-panel-ext-link">SeekersGuidance — Complete Zakat Guide →</a>
              </div>
            </div>

            <div className="nm-panel-cta">
              <p>To give or receive Zakat locally in Bronzeville, contact Masjid Al-Ihsan directly.</p>
              <a href="/subhaal/masjid-al-ihsan/contact" className="nm-panel-cta-btn">Contact the Masjid →</a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Amiri:ital,wght@0,400;0,700;1,400&display=swap');

        .nm-wrap { background: #ffffff; }

        /* ── Welcome Banner ── */
        .nm-welcome {
          position: relative;
          background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 60%, #1e40af 100%);
          padding: 40px 44px;
          margin-bottom: 32px;
          overflow: hidden;
        }
        .nm-welcome::before {
          content: '';
          position: absolute;
          top: -80px; right: -80px;
          width: 320px; height: 320px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%);
        }
        .nm-welcome::after {
          content: '';
          position: absolute;
          bottom: -40px; left: 30%;
          width: 200px; height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%);
        }
        .nm-welcome-label {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
          z-index: 1;
        }
        .nm-welcome-label::before {
          content: '';
          display: inline-block;
          width: 20px; height: 1px;
          background: rgba(255,255,255,0.25);
        }
        .nm-welcome-arabic {
          font-family: 'Amiri', serif;
          font-size: 32px;
          color: rgba(255,255,255,0.55);
          direction: rtl;
          display: block;
          margin-bottom: 10px;
          position: relative;
          z-index: 1;
        }
        .nm-welcome-title {
          font-family: 'Amiri', serif;
          font-size: clamp(22px, 3vw, 32px);
          font-weight: 700;
          color: #fff;
          margin: 0 0 12px;
          position: relative;
          z-index: 1;
          line-height: 1.25;
        }
        .nm-welcome-text {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: rgba(255,255,255,0.7);
          line-height: 1.8;
          max-width: 640px;
          margin: 0;
          position: relative;
          z-index: 1;
        }

        /* ── Accordion ── */
        .nm-accordion { display: flex; flex-direction: column; gap: 10px; }
        .nm-item {
          background: #fff;
          border: 1px solid rgba(30,58,138,0.1);
          overflow: hidden;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .nm-item.open {
          border-color: rgba(30,58,138,0.25);
          box-shadow: 0 4px 24px rgba(30,58,138,0.07);
        }
        .nm-item-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px 24px;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          transition: background 0.15s;
        }
        .nm-item-trigger:hover { background: rgba(30,58,138,0.02); }
        .nm-item-icon {
          font-size: 20px;
          flex-shrink: 0;
          width: 44px; height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(30,58,138,0.05);
          border-radius: 8px;
        }
        .nm-item-text { flex: 1; min-width: 0; }
        .nm-item-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: #1e3a8a;
          margin: 0 0 2px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .nm-item-arabic {
          font-family: 'Amiri', serif;
          font-size: 15px;
          color: rgba(30,58,138,0.35);
          font-weight: 400;
        }
        .nm-item-subtitle {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(30,58,138,0.45);
          margin: 0;
        }
        .nm-item-chevron {
          font-size: 13px;
          color: rgba(30,58,138,0.35);
          transition: transform 0.25s;
          flex-shrink: 0;
        }
        .nm-item.open .nm-item-chevron { transform: rotate(180deg); }
        .nm-item-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.4s ease;
        }
        .nm-item.open .nm-item-content { max-height: 2000px; }
        .nm-item-inner {
          padding: 0 22px 22px;
          border-top: 1px solid rgba(59,130,246,0.08);
        }

        /* ── Name of Allah Header ── */
        .nm-allah-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%);
          padding: 24px 28px;
          position: relative;
          overflow: hidden;
        }
        .nm-allah-header::before {
          content: '';
          position: absolute;
          top: -30px; right: -30px;
          width: 120px; height: 120px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%);
        }
        .nm-allah-header-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 14px;
          position: relative;
          z-index: 1;
        }
        .nm-allah-label {
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.35);
          margin-bottom: 4px;
        }
        .nm-allah-name-en {
          font-family: monospace;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.9);
        }
        .nm-allah-meaning {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(255,255,255,0.5);
          margin-top: 2px;
        }
        .nm-allah-arabic-name {
          font-family: 'Amiri', serif;
          font-size: 36px;
          color: rgba(255,255,255,0.25);
          direction: rtl;
          line-height: 1;
          position: relative;
          z-index: 1;
        }
        .nm-ayah-block {
          background: rgba(255,255,255,0.06);
          border-left: 2px solid rgba(212,175,55,0.5);
          padding: 12px 16px;
          position: relative;
          z-index: 1;
        }
        .nm-ayah-arabic {
          font-family: 'Amiri', serif;
          font-size: 18px;
          color: rgba(255,255,255,0.7);
          direction: rtl;
          line-height: 1.8;
          display: block;
          margin-bottom: 6px;
        }
        .nm-ayah-trans {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(255,255,255,0.5);
          font-style: italic;
          line-height: 1.6;
          margin: 0 0 4px;
        }
        .nm-ayah-ref {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.12em;
          color: rgba(212,175,55,0.6);
        }

        /* ── Fisabilillah Intro ── */
        .nm-fisabilillah {
          padding: 20px 28px;
          background: rgba(30,58,138,0.02);
          border-bottom: 1px solid rgba(30,58,138,0.06);
        }
        .nm-fisabilillah-label {
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.35);
          margin-bottom: 8px;
        }
        .nm-fisabilillah-text {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: rgba(30,58,138,0.65);
          line-height: 1.85;
          margin: 0;
        }

        /* ── Resources ── */
        .nm-resources-section { padding: 20px 28px 28px; }
        .nm-resources-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
        }
        .nm-resources-label {
          font-family: monospace;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.4);
        }
        .nm-resources-line { flex: 1; height: 1px; background: rgba(30,58,138,0.08); }
        .nm-resources-list { display: flex; flex-direction: column; gap: 8px; }

        /* Panel trigger button */
        .nm-resource-panel-btn {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 13px 16px;
          background: linear-gradient(135deg, rgba(30,58,138,0.04) 0%, rgba(30,58,138,0.02) 100%);
          border: 1px solid rgba(30,58,138,0.15);
          text-decoration: none;
          transition: all 0.2s;
          cursor: pointer;
          width: 100%;
          text-align: left;
        }
        .nm-resource-panel-btn:hover {
          background: linear-gradient(135deg, rgba(30,58,138,0.1) 0%, rgba(30,58,138,0.06) 100%);
          border-color: rgba(30,58,138,0.3);
          transform: translateX(2px);
        }
        .nm-resource-panel-icon {
          width: 28px; height: 28px;
          background: #1e3a8a;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 12px;
          color: white;
          margin-top: 2px;
        }

        /* Regular link */
        .nm-resource-link {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 13px 16px;
          background: #fafafa;
          border: 1px solid rgba(30,58,138,0.07);
          text-decoration: none;
          transition: all 0.2s;
        }
        .nm-resource-link:hover {
          background: rgba(30,58,138,0.03);
          border-color: rgba(30,58,138,0.18);
        }
        .nm-resource-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #1d4ed8;
          flex-shrink: 0;
          margin-top: 7px;
        }
        .nm-resource-info { flex: 1; min-width: 0; }
        .nm-resource-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #1e3a8a;
          margin: 0 0 2px;
        }
        .nm-resource-desc {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(30,58,138,0.45);
          margin: 0;
          line-height: 1.5;
        }
        .nm-resource-arrow {
          font-size: 12px;
          color: rgba(30,58,138,0.25);
          flex-shrink: 0;
          margin-top: 3px;
        }
        .nm-resource-panel-badge {
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          background: rgba(30,58,138,0.1);
          color: #1e3a8a;
          padding: 2px 6px;
          border-radius: 2px;
          margin-left: 6px;
          vertical-align: middle;
        }

        /* ── Contact CTA ── */
        .nm-cta {
          margin-top: 28px;
          padding: 24px 28px;
          background: rgba(30,58,138,0.03);
          border: 1px solid rgba(30,58,138,0.1);
          text-align: center;
        }
        .nm-cta-text {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: rgba(30,58,138,0.65);
          margin: 0 0 14px;
          line-height: 1.7;
        }
        .nm-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 26px;
          background: #1e3a8a;
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          transition: background 0.2s;
        }
        .nm-cta-btn:hover { background: #1d4ed8; }

        /* ── SIDE PANEL ── */
        .nm-panel-overlay {
          position: fixed;
          inset: 0;
          background: rgba(13,31,60,0.55);
          z-index: 1000;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }
        .nm-panel-overlay.visible {
          opacity: 1;
          pointer-events: auto;
        }
        .nm-panel-drawer {
          position: fixed;
          top: 0;
          right: 0;
          width: min(600px, 95vw);
          height: 100vh;
          background: #fff;
          z-index: 1001;
          transform: translateX(100%);
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: -8px 0 48px rgba(13,31,60,0.18);
        }
        .nm-panel-drawer.visible { transform: translateX(0); }
        .nm-panel-header {
          background: linear-gradient(135deg, #0d1f3c 0%, #1e3a8a 100%);
          padding: 20px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-shrink: 0;
        }
        .nm-panel-header-title {
          font-family: 'Amiri', serif;
          font-size: 20px;
          font-weight: 700;
          color: #fff;
          margin: 0;
        }
        .nm-panel-close {
          width: 36px; height: 36px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 4px;
          color: rgba(255,255,255,0.7);
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
          flex-shrink: 0;
        }
        .nm-panel-close:hover { background: rgba(255,255,255,0.18); }
        .nm-panel-scroll {
          flex: 1;
          overflow-y: auto;
          overscroll-behavior: contain;
        }
        .nm-panel-body {
          padding: 0;
        }

        /* Panel inner sections */
        .nm-panel-allah-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%);
          padding: 24px 28px;
        }
        .nm-panel-allah-label {
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
          margin-bottom: 6px;
        }
        .nm-panel-allah-name {
          font-family: monospace;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.1em;
          color: rgba(255,255,255,0.92);
          margin-bottom: 4px;
        }
        .nm-panel-allah-meaning {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(255,255,255,0.5);
        }
        .nm-panel-ayah {
          background: rgba(30,58,138,0.04);
          border-left: 3px solid rgba(180,150,80,0.5);
          padding: 16px 24px;
          margin: 0;
        }
        .nm-panel-ayah-arabic {
          font-family: 'Amiri', serif;
          font-size: 20px;
          color: #1e3a8a;
          direction: rtl;
          display: block;
          line-height: 1.8;
          margin-bottom: 8px;
        }
        .nm-panel-ayah-trans {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(30,58,138,0.6);
          font-style: italic;
          line-height: 1.6;
          margin: 0;
        }
        .nm-panel-fisabilillah {
          padding: 20px 28px;
          background: rgba(30,58,138,0.02);
          border-bottom: 1px solid rgba(30,58,138,0.07);
        }
        .nm-panel-fisabilillah-label {
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.35);
          margin-bottom: 8px;
        }
        .nm-panel-fisabilillah p {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: rgba(30,58,138,0.65);
          line-height: 1.85;
          margin: 0;
        }
        .nm-panel-section-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 700;
          color: #1e3a8a;
          padding: 20px 28px 12px;
          border-bottom: 1px solid rgba(30,58,138,0.07);
          letter-spacing: 0.04em;
        }
        .nm-panel-video-wrap {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%;
          background: #0d1f3c;
        }
        .nm-panel-video {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: none;
        }
        .nm-panel-video-caption {
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          color: rgba(30,58,138,0.4);
          padding: 8px 28px 0;
          font-style: italic;
          margin: 0;
        }

        /* Steps */
        .nm-panel-steps { padding: 16px 28px 24px; }
        .nm-panel-step-intro {
          background: rgba(30,58,138,0.03);
          border: 1px solid rgba(30,58,138,0.08);
          padding: 14px 18px;
          margin-bottom: 16px;
        }
        .nm-panel-step-intro p {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(30,58,138,0.65);
          line-height: 1.7;
          margin: 0;
        }
        .nm-panel-step {
          display: flex;
          gap: 14px;
          padding: 12px 0;
          border-bottom: 1px solid rgba(30,58,138,0.06);
        }
        .nm-panel-step:last-of-type { border-bottom: none; }
        .nm-panel-step-num {
          width: 28px; height: 28px;
          background: #1e3a8a;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: monospace;
          font-size: 11px;
          font-weight: 700;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .nm-panel-step-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #1e3a8a;
          margin-bottom: 4px;
        }
        .nm-panel-step-desc {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(30,58,138,0.6);
          line-height: 1.6;
        }
        .nm-panel-step-note {
          background: rgba(180,150,80,0.08);
          border: 1px solid rgba(180,150,80,0.2);
          padding: 12px 16px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(30,58,138,0.65);
          line-height: 1.6;
          margin-top: 12px;
        }

        /* Info block */
        .nm-panel-info-block {
          margin: 20px 28px;
          padding: 18px 20px;
          background: rgba(30,58,138,0.03);
          border: 1px solid rgba(30,58,138,0.1);
        }
        .nm-panel-info-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.5);
          margin-bottom: 10px;
        }
        .nm-panel-info-block p {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(30,58,138,0.65);
          line-height: 1.7;
          margin: 0 0 12px;
        }
        .nm-panel-ext-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: #1d4ed8;
          text-decoration: none;
          transition: gap 0.2s;
        }
        .nm-panel-ext-link:hover { gap: 10px; }

        /* Panel CTA */
        .nm-panel-cta {
          margin: 20px 28px 28px;
          padding: 20px 22px;
          background: rgba(30,58,138,0.04);
          border: 1px solid rgba(30,58,138,0.1);
          text-align: center;
        }
        .nm-panel-cta p {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(30,58,138,0.6);
          margin: 0 0 12px;
          line-height: 1.6;
        }
        .nm-panel-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 22px;
          background: #1e3a8a;
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          text-decoration: none;
          transition: background 0.2s;
        }
        .nm-panel-cta-btn:hover { background: #1d4ed8; }

        /* Five Pillars panel */
        .nm-panel-pillar {
          padding: 20px 28px;
          border-bottom: 1px solid rgba(30,58,138,0.08);
        }
        .nm-panel-pillar:last-of-type { border-bottom: none; }
        .nm-panel-pillar-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 12px;
        }
        .nm-panel-pillar-num {
          width: 36px; height: 36px;
          background: linear-gradient(135deg, #1e3a8a, #1d4ed8);
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Amiri', serif;
          font-size: 18px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .nm-panel-pillar-arabic {
          font-family: 'Amiri', serif;
          font-size: 22px;
          color: rgba(30,58,138,0.4);
          direction: rtl;
          line-height: 1;
          margin-bottom: 4px;
        }
        .nm-panel-pillar-name {
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 700;
          color: #1e3a8a;
          margin-bottom: 2px;
        }
        .nm-panel-pillar-allah {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.12em;
          color: rgba(30,58,138,0.4);
        }
        .nm-panel-pillar-ayah {
          background: rgba(30,58,138,0.03);
          border-left: 2px solid rgba(180,150,80,0.4);
          padding: 10px 14px;
          margin-bottom: 12px;
        }
        .nm-panel-pillar-ayah-arabic {
          font-family: 'Amiri', serif;
          font-size: 16px;
          color: #1e3a8a;
          direction: rtl;
          display: block;
          line-height: 1.7;
          margin-bottom: 6px;
        }
        .nm-panel-pillar-ayah-trans {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(30,58,138,0.55);
          font-style: italic;
          line-height: 1.5;
        }
        .nm-panel-pillar-desc {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(30,58,138,0.65);
          line-height: 1.75;
          margin: 0 0 12px;
        }
        .nm-panel-pillar-resources {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .nm-panel-pillar-resource-btn {
          padding: 7px 14px;
          background: rgba(30,58,138,0.06);
          border: 1px solid rgba(30,58,138,0.15);
          color: #1e3a8a;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
        }
        .nm-panel-pillar-resource-btn:hover {
          background: rgba(30,58,138,0.12);
          border-color: rgba(30,58,138,0.3);
        }
        .nm-panel-pillar-resource-link {
          padding: 7px 14px;
          background: rgba(30,58,138,0.03);
          border: 1px solid rgba(30,58,138,0.1);
          color: #1d4ed8;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }
        .nm-panel-pillar-resource-link:hover {
          background: rgba(30,58,138,0.07);
          border-color: rgba(30,58,138,0.25);
        }

        /* 99 Names grid */
        .nm-panel-names-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          padding: 16px 28px 24px;
        }
        .nm-panel-name-card {
          background: rgba(30,58,138,0.03);
          border: 1px solid rgba(30,58,138,0.08);
          padding: 12px 10px;
          text-align: center;
          transition: all 0.2s;
        }
        .nm-panel-name-card:hover {
          background: rgba(30,58,138,0.07);
          border-color: rgba(30,58,138,0.2);
        }
        .nm-panel-name-num {
          font-family: monospace;
          font-size: 9px;
          color: rgba(30,58,138,0.3);
          margin-bottom: 4px;
        }
        .nm-panel-name-arabic {
          font-family: 'Amiri', serif;
          font-size: 18px;
          color: #1e3a8a;
          direction: rtl;
          line-height: 1.3;
          margin-bottom: 4px;
        }
        .nm-panel-name-trans {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.08em;
          color: rgba(30,58,138,0.5);
          margin-bottom: 3px;
        }
        .nm-panel-name-meaning {
          font-family: 'DM Sans', sans-serif;
          font-size: 10px;
          color: rgba(30,58,138,0.45);
          line-height: 1.4;
        }

        @media (max-width: 640px) {
          .nm-welcome { padding: 28px 20px; }
          .nm-item-trigger { padding: 16px 18px; }
          .nm-allah-header { padding: 18px 20px; }
          .nm-fisabilillah { padding: 16px 20px; }
          .nm-resources-section { padding: 16px 20px 22px; }
          .nm-panel-names-grid { grid-template-columns: repeat(2, 1fr); }
          .nm-panel-steps { padding: 16px 20px 20px; }
          .nm-panel-pillar { padding: 16px 20px; }
        }
      `}</style>

      {/* ── Side Panel Overlay ── */}
      <div
        className={`nm-panel-overlay ${panelVisible ? 'visible' : ''}`}
        onClick={closePanel}
        aria-hidden="true"
      />

      {/* ── Side Panel Drawer ── */}
      <div
        className={`nm-panel-drawer ${panelVisible ? 'visible' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Resource Panel"
      >
        <div className="nm-panel-header">
          <h2 className="nm-panel-header-title">
            {activePanelId === 'how-to-pray-steps' && 'How to Pray — Step by Step'}
            {activePanelId === 'how-to-pray-seekers' && 'How to Pray — SeekersGuidance'}
            {activePanelId === 'salah-yaqeen' && 'Salah — Yaqeen Institute'}
            {activePanelId === 'five-pillars-detail' && 'The Five Pillars of Islam'}
            {activePanelId === 'ninety-nine-names' && '99 Names of Allah'}
            {activePanelId === 'six-pillars-iman' && 'Six Pillars of Iman'}
            {activePanelId === 'marriage-panel' && 'Marriage (Nikah) in Islam'}
            {activePanelId === 'janazah-panel' && 'Janazah — Islamic Funeral Rites'}
            {activePanelId === 'aqidah-fiqh-panel' && 'Aqidah & Fiqh'}
            {activePanelId === 'quran-hadith-panel' && 'Quran & Hadith'}
            {activePanelId === 'zakat-panel' && 'Zakat — Obligatory Charity'}
          </h2>
          <button className="nm-panel-close" onClick={closePanel} aria-label="Close panel">✕</button>
        </div>
        <div className="nm-panel-scroll">
          {activePanelId && renderPanelContent(activePanelId)}
        </div>
      </div>

      <div className="nm-wrap">
        {/* Welcome Banner */}
        <div className="nm-welcome">
          <div className="nm-welcome-label">New Muslims &amp; New Shahadahs</div>
          <span className="nm-welcome-arabic">أَهْلاً وَسَهْلاً بِكُمْ</span>
          <h3 className="nm-welcome-title">Welcome to the Deen — Foundational Resources</h3>
          <p className="nm-welcome-text">
            Masjid Al-Ihsan warmly welcomes those who have taken their Shahadah and those exploring Islam.
            Below you will find foundational resources to help you begin your journey — each category is introduced
            through a Name of Allah, a Quranic ayah, and a contextual reflection. Click any resource marked with a panel icon to open a detailed guide. You are not alone. This community is here for you.
          </p>
        </div>

        {/* Accordion */}
        <div className="nm-accordion">
          {CATEGORIES.map(cat => (
            <div key={cat.id} className={`nm-item ${openId === cat.id ? 'open' : ''}`}>
              <button className="nm-item-trigger" onClick={() => toggle(cat.id)} aria-expanded={openId === cat.id}>
                <div className="nm-item-icon">{cat.icon}</div>
                <div className="nm-item-text">
                  <p className="nm-item-title">
                    {cat.title}
                    <span className="nm-item-arabic">{cat.arabic}</span>
                  </p>
                  <p className="nm-item-subtitle">{cat.subtitle}</p>
                </div>
                <span className="nm-item-chevron">▼</span>
              </button>

              <div className="nm-item-content">
                <div className="nm-item-inner">
                  {/* Name of Allah Header */}
                  <div className="nm-allah-header">
                    <div className="nm-allah-header-top">
                      <div>
                        <div className="nm-allah-label">Name of Allah</div>
                        <div className="nm-allah-name-en">{cat.nameOfAllah}</div>
                        <div className="nm-allah-meaning">{cat.meaning}</div>
                      </div>
                      <div className="nm-allah-arabic-name">{cat.arabicName}</div>
                    </div>
                    <div className="nm-ayah-block">
                      <span className="nm-ayah-arabic">{cat.ayat}</span>
                      <p className="nm-ayah-trans">{cat.ayatTrans}</p>
                      <span className="nm-ayah-ref">{cat.ayatRef}</span>
                    </div>
                  </div>

                  {/* Fisabilillah Intro */}
                  <div className="nm-fisabilillah">
                    <div className="nm-fisabilillah-label">Fisabilillah — In the Way of Allah</div>
                    <p className="nm-fisabilillah-text">{cat.fisabilillah}</p>
                  </div>

                  {/* Resources */}
                  <div className="nm-resources-section">
                    <div className="nm-resources-header">
                      <span className="nm-resources-label">Resources &amp; Links</span>
                      <div className="nm-resources-line" />
                    </div>
                    <div className="nm-resources-list">
                      {cat.resources.map((r, i) => (
                        r.panelId ? (
                          <button
                            key={i}
                            onClick={() => openPanel(r.panelId!)}
                            className="nm-resource-panel-btn"
                          >
                            <div className="nm-resource-panel-icon">▶</div>
                            <div className="nm-resource-info">
                              <p className="nm-resource-title">
                                {r.title}
                                <span className="nm-resource-panel-badge">Open Panel</span>
                              </p>
                              <p className="nm-resource-desc">{r.desc}</p>
                            </div>
                            <span className="nm-resource-arrow">→</span>
                          </button>
                        ) : (
                          <a
                            key={i}
                            href={r.url}
                            target={r.url?.startsWith('http') ? '_blank' : '_self'}
                            rel={r.url?.startsWith('http') ? 'noopener noreferrer' : undefined}
                            className="nm-resource-link"
                          >
                            <div className="nm-resource-dot" />
                            <div className="nm-resource-info">
                              <p className="nm-resource-title">{r.title}</p>
                              <p className="nm-resource-desc">{r.desc}</p>
                            </div>
                            <span className="nm-resource-arrow">→</span>
                          </a>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="nm-cta">
          <p className="nm-cta-text">
            Have questions or need personal guidance? Imam Madyun and the Masjid Al-Ihsan community are here to help.
          </p>
          <a href="/subhaal/masjid-al-ihsan/contact" className="nm-cta-btn">
            Contact the Masjid →
          </a>
        </div>
      </div>
    </>
  );
}
